import { type UrlObject } from 'url';
import {
  CONVEYANCE_TYPES,
  type Basket,
  PAYMENT_TYPES,
  type PaymentDetails,
  type OrderDetails,
} from '@koala/sdk';
import {
  createBasket,
  addTip,
  setInstructions,
  setUtensils,
  setConveyance,
  setPickupTime,
  submitOrder,
  validateBasket,
  removePickupTime,
} from '@koala/sdk/v4';
import Router from 'next/router';
import {
  all,
  call,
  put,
  spawn,
  take,
  select,
  takeLeading,
  takeLatest,
  type SagaReturnType,
} from 'redux-saga/effects';
import { type BasketOrder } from '@koala/sdk/v4';
import actions from '@/redux/orderStatus/actions';
import { genericEventHandler } from '@/analytics/events';
import { EventNames, GlobalEvents } from '@/analytics/events/constants';
import { InitializationErrorTypes, InitializationSteps } from '@/constants/basket';
import { DATE_FORMAT } from '@/constants/dates';
import { GENERIC_ERROR_COPY } from '@/constants/fpoErrorCopy';
import { API_CONVEYANCE_TYPES, ERROR_MESSAGES } from '@/constants/events';
import { ASAP_VALUE, DEFAULT_GUEST_USER } from '@/constants/global';
import { ROUTES } from '@/constants/routes';
import { OrderStatus } from '@/constants/status';
import basketActions from '@/redux/basket/actions';
import orderStatusActions from '@/redux/orderStatus/actions';
import promoCodeActions from '@/redux/promoCode/actions';
import commerceActions from '@/redux/commerce/actions';
import conveyanceModeActions from '@/redux/conveyanceMode/actions';
import { surfaceConveyanceGatewayFailure } from '@/redux/conveyanceMode/gatewayFailure';
import { surfaceBasketNotFound } from '@/redux/orderStatus/basketNotFound';
import globalActions from '@/redux/global/actions';
import paymentActions from '@/redux/payment/actions';
import timeslotsActions from '@/redux/timeslots/actions';
import { createHttpClient } from '@/services/client';
import { type RootState } from '@/types/app';
import {
  OrderStatuses,
  OrderStatusMessages,
  type PreAuthValidation,
  SubmitFailureCause,
} from '@/types/orderStatus';
import { getOrigin } from '@/utils';
import { patchBasketProductOptions } from '@/utils/basket';
import {
  assembleDeliveryObjectFromState,
  assembleOrderDataFromState,
  getConveyanceCheck,
  getIdsFromState,
  getInitializationErrorType,
  getLocationFromState,
} from '@/utils/checkout';
import { isDateSameOrAfter, formatDate } from '@/utils/dates';
import * as ErrorReporter from '@/utils/errorReporter';
import { TOAST_HOLD_RELEASE_NOTE } from '@/utils/errorReporter';
import {
  classifySubmitFailure,
  getUnavailableBasketItemIds,
  INVALID_EMAIL_SUBMIT_MESSAGE,
  INVALID_EMAIL_USER_MESSAGE,
  isDeliveryUnavailableError,
  isInvalidEmailError,
  isPricingUnavailableError,
  getRequiredOptionGroupName,
  getRestaurantUnavailableReason,
  isRequiredOptionMissingError,
  isRestaurantUnavailableError,
  resolveRequiredOptionBasketItemIndex,
} from '@/utils/errors';
import { isDinerFacingError, DinerFacingError } from '@/utils/dinerFacingError';
import { prepareErrorMessage, sessionStorageBasket } from '@/utils/global';
import { fireGaEvent, gaActions, gaCats } from '@/utils/googleAnalytics';
import { determineHourType } from '@/utils/wantedAt';
import { convertConveyanceToDeliveryAddress } from '@/utils/deliveryStatus';
import { buildFulfillmentFromBasket } from '@/utils/fulfillment';
import { queryKeys } from '@/features/configs/queries';
import { safeHeapTrack } from '@/utils/analytics';
import { isAsapValue } from '@/utils/conveyance';
import { getMappedConveyanceType } from '@/features/handoff/time-picker/utils';
import { type IConveyanceDeliveryAddress } from '@/types/fulfillment';
import {
  getBasketLogData,
  getCompletedOrderLogData,
  getPaymentLogData,
  sentryLogger,
} from '@/utils/sentryLogger';
import {
  isPaymentError,
  paymentReducerDigitalWalletMap,
  resolveDeclineCopy,
} from '@/utils/payment';
import {
  isStatusError,
  isRetryableError,
  isNetworkError,
  isNonJsonResponseError,
  GATEWAY_STATUSES,
} from '@/utils/networkErrors';
import { retryOnceOnStatuses } from '@/utils/requests';
import { clearSavedCart } from '@/utils/savedCart';
import { PAYMENT_PROVIDER } from '@/constants/payment';
import { paymentVerificationSaga } from '@/redux/orderStatus/paymentVerificationSaga';
import {
  getPaymentVerificationChallenge,
  hasCardTokenPayment,
  isVerificationResume,
  redactVerificationSecret,
} from '@/utils/paymentVerification';

/**
 * Builds the optional `conveyance` payload to include on the initial createBasket call so
 * the basket is born with the correct conveyance instead of requiring a separate setConveyance
 * round-trip (TEAM-14698). Returns null when the guard conditions that would cause the
 * existing setConveyance flow to skip/throw are unmet, so we fall back cleanly.
 */
function buildInitialConveyanceForCreate(
  conveyanceType: string,
  deliveryAddress: IConveyanceDeliveryAddress | null,
): BasketOrder['conveyance'] | null {
  if (conveyanceType === CONVEYANCE_TYPES.DELIVERY) {
    // Mirror the guards in the DELIVERY branch below: address must exist, zip must be
    // well-formed, and phone_number must be present or the backend rejects the conveyance.
    if (!deliveryAddress) return null;
    if (deliveryAddress.zip_code && deliveryAddress.zip_code.length > 6) return null;
    if (!deliveryAddress.phone_number) return null;
    return {
      type: API_CONVEYANCE_TYPES.DELIVERY,
      options: { delivery_address: convertConveyanceToDeliveryAddress(deliveryAddress) },
    };
  }
  if (conveyanceType === CONVEYANCE_TYPES.PICKUP) {
    return { type: API_CONVEYANCE_TYPES.PICKUP };
  }
  return null;
}

/** Keeps a long upstream error body from blowing Google Analytics' event-label limit. */
const GA_LABEL_DETAIL_MAX_LENGTH = 200;

/**
 * Step-scoped prefix for a wanted-at failure. Deliberately not in `FPO_ERROR_COPY`, which is keyed
 * by FPO error code — this is keyed by initialization step.
 */
const WANTED_AT_PREFIX = 'There was an issue setting your desired ready time.';

/**
 * The validate failures that open the `validate` recovery modal (TEAM-14320 — see the rationale in
 * `runBasketValidation`'s catch, including why this is NOT `isRetryableError`). Shared with the
 * setup validate in `initializeOrderSaga` (TEAM-14787) so the two cannot drift apart.
 */
const opensValidateRecovery = (error: unknown): boolean =>
  isStatusError(error, GATEWAY_STATUSES) || isNetworkError(error) || isNonJsonResponseError(error);

/**
 * Pushes a basket response into redux on the catch-block paths: `checkoutBasket`, the
 * item-store sync, and the post-init calls. `response` is optional because the catch block is
 * reachable before anything assigned it, so the guard lives here rather than at each call site
 * (TEAM-14375). Consume with `yield*` — the body yields effects.
 */
function* hydrateBasketFromResponse(
  response: Basket | undefined,
  action: ReturnType<typeof actions.initializeOrder>,
  conveyanceMode: RootState['app']['conveyanceMode'],
  wantedAtDay: string | undefined,
) {
  if (!response) return;

  yield put(basketActions.success(response));

  if (response.basket_items) {
    yield put(basketActions.basketItemsSyncedWithStore(response.basket_items));
  }

  yield put(
    actions.postInitializeOrder(
      action.basketOrder,
      response,
      conveyanceMode.type,
      wantedAtDay,
      action.wantedAt,
    ),
  );
}

/**
 * Initialize Basket
 * This is used to initialize and update the (checkout/remote) basket
 * @TODO Saga is doing to much, split into smaller sagas or util functions
 */
export function* initializeOrderSaga(action: ReturnType<typeof actions.initializeOrder>) {
  let response: Basket;
  let wantedAtDay;
  let initializationStep = InitializationSteps.CREATE_BASKET;
  // Initialized so the catch can tell "no basket yet" (createBasket threw) from a known id.
  let basketId: string | null = null;
  const state = (yield select()) as RootState;
  const promoCode = state.app.promoCode;
  const checkoutBasket = state.app.basket.checkoutBasket;
  const appliedPromoCode = promoCode.applied;
  const me = state.app.me.data;
  const conveyanceMode = state.app.conveyanceMode;
  const localBasket = sessionStorageBasket.get();

  try {
    const guestUser = state.app.auth.guestUser;
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    // Track whether the create call already applied conveyance so we can skip the redundant
    // setConveyance round-trip below (TEAM-14698). Falls back to the pre-existing flow if
    // the backend response doesn't reflect the conveyance we sent.
    let conveyanceAppliedOnCreate = false;

    /**
     * If a remote basket has been created with the ordering provider,
     * don't create a new basket—just reuse the existing one.
     */
    if (localBasket?.checkoutBasket?.id) {
      basketId = localBasket.checkoutBasket.id;
    } else {
      /**
       * 1. Create a new order — include the guest's already-selected conveyance so the basket
       * is born with it applied instead of needing a follow-up setConveyance call.
       */
      const basket = patchBasketProductOptions(action.basketOrder);
      const initialDeliveryAddress =
        conveyanceMode.type === CONVEYANCE_TYPES.DELIVERY
          ? assembleDeliveryObjectFromState(state)
          : null;
      const conveyanceForCreate = buildInitialConveyanceForCreate(
        conveyanceMode.type,
        initialDeliveryAddress,
      );
      const basketWithConveyance = conveyanceForCreate
        ? { ...basket, conveyance: conveyanceForCreate }
        : basket;

      response = (yield call(createBasket, basketWithConveyance, action.locationId, {
        client,
      })) as Basket;
      basketId = response.id;

      // Only skip the follow-up setConveyance if the backend echoed the conveyance back —
      // otherwise (older backend, feature off) fall through to the existing flow.
      // Verify address was saved on the backend basket
      if (conveyanceForCreate) {
        const typeEchoed =
          getMappedConveyanceType(response.conveyance_type?.type ?? '') === conveyanceMode.type;
        const addressEchoed =
          conveyanceMode.type === CONVEYANCE_TYPES.DELIVERY
            ? Boolean(response.conveyance_type?.delivery_address?.street_address)
            : true;
        conveyanceAppliedOnCreate = typeEchoed && addressEchoed;
      }
    }

    /**
     * 2. Persist wanted at on the server
     * If a user has persisted a non-asap wanted-at, we need to attempt
     */
    if (action.wantedAt) {
      // Ensure the wanted at time is in the future
      if (isDateSameOrAfter(action.wantedAt, new Date())) {
        // Format wantedAtDay from wantedAt timestamp so that we
        // can request basket hours for the appropriate day
        wantedAtDay = formatDate(action.wantedAt, DATE_FORMAT.YEAR_MONTH_DAY);
        initializationStep = InitializationSteps.SET_WANTED_AT;
        // Capture the response: this is the only call that writes `response` on the
        // reused-basket + scheduled-time path, so without it a later throw reaches the catch
        // block with `response` unassigned and the basket never gets hydrated.
        response = (yield call(
          setPickupTime,
          {
            basketId,
            locationId: action.locationId,
            wanted_at: action.wantedAt,
          },
          { client },
        )) as SagaReturnType<typeof setPickupTime>;

        // If call succeeds, update the timeslots reducer
        yield put(timeslotsActions.setWantedAt(action.wantedAt));
      }
    }
    /**
     * TEAM-14375: only clear the server-side wanted_at when there is one to clear.
     * `isAsapValue` is `!time || time === ASAP_VALUE` and `ASAP_VALUE` is `''`, so it reduces
     * to `!time`, and cart/footer.tsx passes `undefined` for a plain ASAP order — so this
     * branch fired a DELETE /wanted-time on every checkout entry. That DELETE runs a
     * submission-grade revalidation which unrelated bad basket/store state fails, surfacing
     * the reconciliation modal on orders with nothing wrong with their time.
     *
     * Two signals, because neither alone is safe:
     *
     * - `localBasket.checkoutBasket.wanted_at` — the persisted basket. Survives a page load,
     *   but it is only written by BASKET_SUCCESS, so a `setPickupTime` that succeeded and was
     *   then followed by a throw before `validateBasket` never reaches it.
     * - `state.app.timeslots.wantedAt` — dispatched immediately after a successful
     *   `setPickupTime` above, before anything can throw, so it witnesses "the server holds a
     *   time" no matter how this saga exits. That is the case the clear-to-ASAP recovery
     *   modals exist for: without it the diner picks ASAP, the DELETE is skipped, and the
     *   order submits at the stale scheduled time while the UI reads ASAP.
     *
     * Both are cleared after a successful remove (below, and in wantedAtBasketRemoveSaga), or
     * the gate would stop skipping and the fix would do nothing. `timeslots.wantedAt` is also
     * reset with the basket itself (DESTROY_BASKET / INIT_NEW_BASKET in timeslots/reducer.ts)
     * so it cannot leak a previous order's time into this one.
     *
     * Both signals are read only when we actually reused the persisted basket: a basket
     * `createBasket` just minted cannot be holding a time, and `timeslots.wantedAt` carries no
     * basket id of its own, so honouring it there would fire the very DELETE this gate removes.
     *
     * The fallback is `||`, not `??`. `wanted_at` is `z.string().nullable()`, and an ASAP basket
     * reaches us as `null` *or* `''` (the SDK's own `basketFixture` defaults to `''`, and every
     * other reader in this app — pre-submit-message, ConveyanceReconciliation, sentryLogger —
     * tests it for truthiness). With `??`, a persisted `''` would short-circuit the witness and
     * silently skip the DELETE on the one path it exists for.
     *
     * See "Clearing a time back to ASAP is gated" in docs/CONVEYANCE.md.
     */
    const persistedBasket = localBasket?.checkoutBasket;
    const serverMayHoldWantedAt = Boolean(
      persistedBasket?.id && (persistedBasket.wanted_at || state.app.timeslots.wantedAt),
    );

    if (isAsapValue(action.wantedAt)) {
      if (serverMayHoldWantedAt) {
        initializationStep = InitializationSteps.REMOVE_WANTED_AT;
        response = (yield call(
          removePickupTime,
          { basketId, locationId: action.locationId },
          { client },
        )) as SagaReturnType<typeof removePickupTime>;

        // Keep the server-witness signal honest, or every later checkout entry re-fires the
        // DELETE this ticket exists to remove.
        yield put(timeslotsActions.setWantedAt(null));
      }

      /**
       * Stays outside the gate, and sourced from `action.locationId` (already a `number`)
       * rather than the response we may not have made: redux must still record ASAP when the
       * DELETE is skipped. That is what makes the gate behaviour-neutral for the diner.
       */
      yield put(
        conveyanceModeActions.setWantedTime(ASAP_VALUE, action.locationId, conveyanceMode.type),
      );
    }

    /**
     * 3. Set conveyance
     */
    switch (conveyanceMode.type) {
      case CONVEYANCE_TYPES.DELIVERY:
        // TEAM-14787 (review round 2): the step starts here, not at the setConveyance call below.
        // The address / zip pre-checks throw before that call, and recorded as CREATE_BASKET they
        // made the summary's Try again re-validate a basket that never received the delivery
        // choice, instead of re-running initialization.
        initializationStep = InitializationSteps.SET_CONVEYANCE;
        const deliveryAddress = assembleDeliveryObjectFromState(state);

        /**
         * TEAM-14699: report missing address state as missing address state. These are two
         * different problems and only one of them is about the zip code — telling a diner
         * with no stored address that their zip is too long sends them to fix a field that
         * is not wrong, and there is nothing they can do to proceed.
         */
        if (!deliveryAddress) {
          throw new DinerFacingError(
            'A delivery address is required for this order.',
            InitializationErrorTypes.HANDLED,
          );
        }

        // Catch any outstanding issues with zip code length
        /** @TODO nail down delivery address type to ensure zip_code is present. */
        if (deliveryAddress.zip_code?.length > 6) {
          throw new DinerFacingError(
            'Please ensure that your delivery zip code is not longer than 6 digits.',
            InitializationErrorTypes.HANDLED,
          );
        }
        if (!deliveryAddress?.phone_number) {
          break;
        }

        // Skip the redundant setConveyance call when the createBasket step already applied
        // the delivery conveyance (TEAM-14698). Falls back to the existing round-trip when
        // the backend response didn't reflect it (older deploy, feature off, reused basket).
        if (!conveyanceAppliedOnCreate) {
          // TEAM-15341: same single gateway retry as the other setConveyance sites; a second
          // 502 is surfaced by the SET_CONVEYANCE branch at the top of the catch below.
          response = yield* retryOnceOnStatuses(
            setConveyance,
            { statuses: GATEWAY_STATUSES },
            {
              basketId,
              locationId: action.locationId,
              type: API_CONVEYANCE_TYPES.DELIVERY,
              data: convertConveyanceToDeliveryAddress(deliveryAddress),
            },
            { client },
          );

          yield put(basketActions.success(response));
        }

        // Update handoff details in redux
        yield put(
          conveyanceModeActions.handoffDetailsSet(CONVEYANCE_TYPES.DELIVERY, {
            instructions: deliveryAddress?.special_instructions ?? '',
          }),
        );
        break;
      default:
        break;
    }

    // Validate Promo code conditions
    if (checkoutBasket?.promo_codes?.length && appliedPromoCode) {
      yield put(
        orderStatusActions.promoCodeAdd(appliedPromoCode, { bypassBasketMutationGuard: true }),
      );
      yield take(orderStatusActions.PROMO_CODE_ADD);
      yield take(orderStatusActions.ORDER_PENDING);
      yield put(orderStatusActions.initializeOrderResume());
    } else {
      yield put(promoCodeActions.promoCodeError(''));
    }
    /**
     * 4. Validate the basket
     *
     */
    const updatedGuestUser = me?.id ? null : guestUser ?? DEFAULT_GUEST_USER;
    initializationStep = InitializationSteps.VALIDATE;
    response = (yield call(
      validateBasket,
      { basketId, locationId: action.locationId, guestUser: updatedGuestUser },
      { client },
    )) as Basket;

    // Update checkoutBasket in redux
    yield all([
      put(basketActions.success(response)),
      put(basketActions.basketItemsSyncedWithStore(response.basket_items)),
      // TEAM-14787: this response is the first one that carries a real price.
      put(actions.basketPricingSettled(basketId, 'priced')),
    ]);

    // Trigger post-initialization calls
    yield put(
      actions.postInitializeOrder(
        action.basketOrder,
        response,
        conveyanceMode.type,
        wantedAtDay,
        action.wantedAt,
      ),
    );
  } catch (error) {
    // TEAM-14787: whatever failed, this initialization did not price the basket. Settled first,
    // before anything below that could itself throw, so no failure class can leave the summary on
    // the create response's zero. A no-op when the basket was already priced (the reducer keeps
    // 'priced').
    if (basketId) {
      yield put(actions.basketPricingSettled(basketId, 'failed', initializationStep));
    }

    /**
     * TEAM-15690: coo-api answered the setup validate with 404 — it no longer has this basket, so
     * every affordance below (Restart Order, the 'validate' recovery modal) would retry the same
     * basket id and fail the same way. End on the "Start a new cart" notice instead, which also
     * skips the INIT_CHECKOUT_UNHANDLED_FAILURE capture and the ORDER_PENDING re-arm at the foot of
     * this catch. Nothing is hydrated: the basket the diner would be shown is the one that is gone.
     *
     * VALIDATE only, by scope: a 404 at SET_WANTED_AT / REMOVE_WANTED_AT / SET_CONVEYANCE keeps
     * today's handling.
     */
    if (
      initializationStep === InitializationSteps.VALIDATE &&
      (yield* surfaceBasketNotFound(error, {
        basketId,
        locationId: action.locationId,
        source: 'init_validate',
      }))
    ) {
      return;
    }

    /**
     * TEAM-15341: the delivery handoff could not be saved because coo-api's gateway failed
     * twice. Show the cart-preserving conveyance recovery modal, as every other setConveyance
     * site does, instead of the reconciliation modal below. The basket is hydrated first so
     * checkout keeps the cart; its saved handoff may still be pickup, which the re-save at
     * Place Order (`reconcileConveyanceBeforeSubmit`) repairs. Any other failure on this step
     * (a 422, a missing address) keeps the handling below unchanged.
     */
    if (
      initializationStep === InitializationSteps.SET_CONVEYANCE &&
      isStatusError(error, GATEWAY_STATUSES)
    ) {
      // @ts-expect-error TS2454: `response` may be unassigned; hydrateBasketFromResponse guards it.
      yield* hydrateBasketFromResponse(response, action, conveyanceMode, wantedAtDay);
      yield* surfaceConveyanceGatewayFailure(error);
      return;
    }

    const preparedErrorResponse = (yield call(
      prepareErrorMessage,
      OrderStatusMessages.INITIALIZING_ORDER_ERROR,
      error,
    )) as SagaReturnType<typeof prepareErrorMessage>;

    const unavailableBasketItemIds = getUnavailableBasketItemIds(preparedErrorResponse);

    let errorEvent;

    // Diner-safe copy, resolved by the TEAM-13882 render rule. This is what gets displayed.
    let errorMessage = preparedErrorResponse.message;
    /**
     * TEAM-13882: the raw upstream text. Every classification below reads this rather than
     * `errorMessage`, because `INITIALIZATION_ERRORS` is a table of English substrings that only
     * ever appear in the backend's `error_description` — matching the resolved copy would classify
     * every initialization failure as UNHANDLED and silently strip the reconciliation modal of its
     * time-picker, phone-capture form and lock handling.
     */
    const errorDetail = preparedErrorResponse.detail;
    /**
     * The substring table's verdict on the raw detail. Read in two places below, and deliberately
     * *not* the same value as `initErrorType`: that one can be overridden by a `DinerFacingError`
     * and reassigned for an unhandled wanted-at failure.
     */
    const detailErrorType = getInitializationErrorType(errorDetail);

    // Machine-readable backend top-level error code (the `error` field). For
    // unavailable-item errors coo-api returns `items_out_of_hours` (all flagged
    // items are out-of-hours — a time-based menu transition) vs
    // `items_unavailable` (suspended/missing — must be removed). The notice
    // branches on this to explain a menu-time change vs a plain removal.
    const errorCode = preparedErrorResponse.error?.error;

    /**
     * TEAM-15138: coo-api rejected a cart line for leaving a required option group below its
     * `min_selections`. Hoisted above the unavailable-items split because it is read **twice** —
     * once to classify (below) and once by the Sentry suppression at the end of this catch, which
     * is outside the `else` this used to be scoped to.
     *
     * Deliberately the **slug predicate** rather than `initErrorType === REQUIRED_OPTION`. The
     * classification is block-scoped, and it can also be reached by a `DinerFacingError` that
     * declares that type for itself — widening the suppression to whatever some future call site
     * asserts, rather than to the one coo-api rejection whose volume this is about.
     */
    const requiredOptionMissing = isRequiredOptionMissingError(preparedErrorResponse);

    /**
     * TEAM-14493: the restaurant's POS could not price this order (FPO code 13). Terminal, so it
     * owns the end state outright: it suppresses the reconciliation modal below (whose affordances
     * are all "try again" in some form) and the delivery- / restaurant-unavailable dispatches, so
     * one envelope can never leave the diner on two blocking surfaces. Matched on the code alone.
     */
    const pricingUnavailable = isPricingUnavailableError(preparedErrorResponse);

    if (unavailableBasketItemIds.length) {
      // Surface unavailable items via the inline notice rather than the
      // reconciliation modal so the user can act on them in place.
      yield put(basketActions.setUnavailableBasketItems(unavailableBasketItemIds, errorCode));

      // @ts-expect-error TS2454: `response` is declared `Basket` but may be unassigned if
      // createBasket itself threw. The guard lives inside hydrateBasketFromResponse.
      yield* hydrateBasketFromResponse(response, action, conveyanceMode, wantedAtDay);

      errorEvent = ERROR_MESSAGES.INIT_CHECKOUT_HANDLED_FAILURE;
    } else {
      /**
       * TEAM-15138: coo-api names this failure in the top-level `error` slug, so it is classified
       * from that rather than from `INITIALIZATION_ERRORS`. It has to win over `detailErrorType`,
       * which would otherwise return UNHANDLED — the message interpolates an operator-authored
       * option group name, so it matches no substring in that table and never will.
       *
       * Left UNHANDLED, this lands on the "Restart Order" affordance, which re-dispatches
       * `initializeOrder` with the same basket and fails identically: a retry loop wearing coo-api's
       * correct sentence. The recovery is to reopen the named cart line, so the line is resolved
       * here — where the basket is in hand — and passed to the modal as an answer rather than as an
       * envelope for it to interpret.
       *
       * `requiredOptionMissing` itself is resolved above the split — see there for why.
       */
      const requiredOption = requiredOptionMissing
        ? {
            basketItemIndex: resolveRequiredOptionBasketItemIndex(
              preparedErrorResponse,
              sessionStorageBasket.get()?.content?.basket_items ?? [],
            ),
            groupName: getRequiredOptionGroupName(preparedErrorResponse),
          }
        : undefined;

      /**
       * A `DinerFacingError` states its own classification, so we don't have to infer it by
       * substring-matching copy we authored ourselves.
       */
      let initErrorType = requiredOptionMissing
        ? InitializationErrorTypes.REQUIRED_OPTION
        : isDinerFacingError(error)
          ? error.initializationErrorType ?? InitializationErrorTypes.UNHANDLED
          : detailErrorType;

      // Determine if basket reconciliation is needed based on error message
      switch (initErrorType) {
        case InitializationErrorTypes.REQUIRED_OPTION:
          // TEAM-15138. Reported under the handled bucket because it is exactly that: a rejection
          // coo-api names, with a recovery the storefront offers.
          errorEvent = ERROR_MESSAGES.INIT_CHECKOUT_HANDLED_FAILURE;
          break;
        case InitializationErrorTypes.HANDLED:
          // If it is a handled reconciliation-specific error
          errorEvent = ERROR_MESSAGES.INIT_CHECKOUT_HANDLED_FAILURE;
          break;
        case InitializationErrorTypes.THROTTLING:
          // If it is a throttling-specific error
          errorEvent = ERROR_MESSAGES.INIT_CHECKOUT_THROTTLING_FAILURE;
          break;
        case InitializationErrorTypes.PHONE:
          // If it is a handled reconciliation-specific error
          errorEvent = ERROR_MESSAGES.INIT_CHECKOUT_PHONE_FAILURE;
          break;
        // TIME_OUT_OF_RANGE: stale-time clearing now lives in refreshBasketMenuSaga
        // (TEAM-14594), which is where `isWantedAtValidationError` actually fires —
        // the basket endpoints called by this saga never return the `wanted-at`
        // validation shape. See PR #792 review rounds 7–8.
        case InitializationErrorTypes.LOCK:
          // If it is a lock-specific error, update log event
          errorEvent = ERROR_MESSAGES.INIT_CHECKOUT_LOCK_FAILURE;
          break;
        case InitializationErrorTypes.UNHANDLED:
          // For unhandled errors, determine if basket reconciliation is needed based on initialization step
          switch (initializationStep) {
            case InitializationSteps.CREATE_BASKET:
              // Create basket failures cannot proceed
              errorEvent = ERROR_MESSAGES.INIT_CHECKOUT_UNHANDLED_BASKET_FAILURE;
              break;
            case InitializationSteps.SET_WANTED_AT:
              /**
               * Wanted at failures cannot proceed. When nothing more specific resolved, say what the
               * diner can do rather than prefixing the generic sentence onto itself — "There was an
               * issue setting your desired ready time. Something went wrong. Please try again."
               */
              errorMessage =
                errorMessage === GENERIC_ERROR_COPY
                  ? `${WANTED_AT_PREFIX} Please choose another time.`
                  : `${WANTED_AT_PREFIX} ${errorMessage}`;
              /**
               * TEAM-13882: this reclassification used to happen by accident. The prefix above is
               * itself a THROTTLING key in `INITIALIZATION_ERRORS`, so the reconciliation modal —
               * which re-derived the type from the message it was about to render — saw THROTTLING
               * and offered the time picker, while the saga had already recorded UNHANDLED. Now
               * that the classification is passed rather than re-derived, say it out loud: an
               * unhandled wanted-at failure is resolvable by choosing another time.
               */
              initErrorType = InitializationErrorTypes.THROTTLING;
              errorEvent = ERROR_MESSAGES.INIT_CHECKOUT_UNHANDLED_WANTED_AT_FAILURE;
              break;
            case InitializationSteps.REMOVE_WANTED_AT:
              /**
               * TEAM-14375: clear-path failures used to arrive here still labelled
               * CREATE_BASKET, because `initializationStep` was never reassigned before the
               * DELETE. They now get their own bucket and copy that matches what the diner
               * actually did — they cleared a time, they did not set one.
               */
              errorMessage = `There was an issue clearing your selected ready time. ${errorMessage}`;
              errorEvent = ERROR_MESSAGES.INIT_CHECKOUT_UNHANDLED_REMOVE_WANTED_AT_FAILURE;
              break;
            case InitializationSteps.SET_CONVEYANCE:
              // Conveyance failures cannot proceed
              errorEvent = ERROR_MESSAGES.INIT_CHECKOUT_UNHANDLED_CONVEYANCE_FAILURE;
              break;
            default:
              // Otherwise, attempt to proceed normally by updating checkoutBasket in redux
              /** @TODO modify request changing flow to ensure `response` is defined. */

              /**
               * TEAM-14375: guarded, mirroring the unavailable-items branch above. A VALIDATE
               * failure lands here with `response` genuinely unassigned on the reused-basket
               * path. See "`response` is not guaranteed to be assigned" in docs/CHECKOUT_PAGE.md
               * for why an unguarded dispatch wipes `checkoutBasket`.
               */
              // @ts-expect-error TS2454: `response` may be unassigned here. The guard lives
              // inside hydrateBasketFromResponse.
              yield* hydrateBasketFromResponse(response, action, conveyanceMode, wantedAtDay);

              /**
               * TEAM-14787: only VALIDATE reaches this branch, and the basket just hydrated is
               * unpriced (on a new basket, the create response's zero default — the summary gates
               * on `basketPricing` instead). The hydrate stays because the retry needs the basket
               * id. Transport failures open the same recovery modal as `runBasketValidation`; no
               * reconciliation and no new Sentry event name (TEAM-14788 owns these captures).
               */
              if (opensValidateRecovery(error)) {
                yield put(actions.recovery502Triggered('validate'));
              }
          }

          break;
      }

      // TEAM-14642 (AC12) is met by TEAM-14375's dedicated REMOVE_WANTED_AT step rather than
      // by this branch's interim flag: the ASAP removal now advances `initializationStep`, so a
      // failure there reports under INIT_CHECKOUT_UNHANDLED_REMOVE_WANTED_AT_FAILURE instead of
      // basket creation, which is the criterion — with copy that matches a removal as well.

      // Fire reconciliation modal if we've assigned a relevant error event — but never for a
      // pricing failure (TEAM-14493): every affordance that modal offers is a retry, and none can
      // succeed. The pricing-unavailable modal dispatched below is the end state instead.
      if (errorEvent && !pricingUnavailable) {
        yield put(
          actions.initializeOrderReconciliation(errorMessage, initErrorType, requiredOption),
        );
      }
    }

    if (errorDetail.includes('prep time is outside menu hours')) {
      yield put(orderStatusActions.orderTimeUnavailableSet());
    }

    if (pricingUnavailable) {
      yield put(actions.pricingUnavailable());
    } else if (isDeliveryUnavailableError(preparedErrorResponse)) {
      yield put(actions.deliveryUnavailable());
    }

    // TEAM-14911: a recognised restaurant-unavailable state (coo-api POS refusal, or a chowly-api
    // RCC-paused / ordering-disabled location) is a known diner-facing condition we surface via the
    // modal — not an unhandled failure. Classify it here and skip the Sentry capture below so it
    // stops flooding COO-WEBAPP-3ST (chowly-api pause) and COO-WEBAPP-3RV (coo-api refusal) as
    // unhandled exceptions. State precedence (AC3) — a closed-by-hours cause outranks this — is
    // centralized in the reducer's RESTAURANT_UNAVAILABLE case, so it holds for all three dispatch
    // sites (init / validate / submit) without a guard here.
    //
    // The flag is read again by the Sentry suppression at the end of this catch, so it stays a
    // const rather than being inlined into the condition.
    // TEAM-14493: a pricing failure outranks this — see `pricingUnavailable` above.
    const isRestaurantUnavailable =
      !pricingUnavailable && isRestaurantUnavailableError(preparedErrorResponse);
    if (isRestaurantUnavailable) {
      // Dispatched AFTER the reconciliation dispatch above, so this is the state the
      // diner ends on — the blocking modal, not the dismissible retry dialog (AC8).
      yield put(
        actions.restaurantUnavailable(getRestaurantUnavailableReason(preparedErrorResponse)),
      );
    }

    // If error results from a stored address, record the address ID
    const state = (yield select()) as RootState;
    const deliveryAddress = assembleDeliveryObjectFromState(state);
    let additionalDetails: string | undefined;

    if (deliveryAddress?.id) {
      additionalDetails = `Stored Address ID: ${deliveryAddress.id}`;

      // If we run into an error setting conveyance, clear ID from address. At worst, we create a duplicate address and don't prevent checkout
      if (detailErrorType === InitializationErrorTypes.LOCK) {
        ErrorReporter.captureMessage(
          'User encountered error checking out with a stored address',
          'info',
          {
            additionalDetails,
            // TEAM-13882: the raw upstream text. `errorMessage` is resolved diner copy and collapses
            // every failure onto the same sentence, which would leave this diagnostic with no
            // technical field at all.
            errorDetail,
          },
        );

        const location = getLocationFromState(state);
        const loggedOutAddress = Object.assign({}, deliveryAddress);
        delete loggedOutAddress.id;
        delete loggedOutAddress.default;

        yield put(
          conveyanceModeActions.setDeliveryAddress(
            convertConveyanceToDeliveryAddress(loggedOutAddress),
            location,
            false,
          ),
        );
      }
    }

    /**
     * The rejections this catch **recognises**, as opposed to the failures it merely survives.
     *
     * TEAM-14911 established the rule for one member and stated it as a special case; TEAM-15138
     * is the second, so it is named here rather than grown into a compound `if`. A member is a
     * backend rejection that (a) coo-api identifies precisely, and (b) this storefront answers
     * with a diner-facing affordance. Neither is an unhandled exception, and Sentry is where
     * unhandled exceptions go.
     *
     * - **restaurant unavailable** (TEAM-14911) — the blocking `ServiceUnavailable` modal.
     *   Retired the COO-WEBAPP-3ST / COO-WEBAPP-3RV floods.
     * - **`required_option_missing`** (TEAM-15138) — the reconciliation modal's "Choose your
     *   {group}" reopen. coo-api#368 marks its own exception `isCustomerFriendly`, which is what
     *   suppresses **COO-API-2HA** (447 occurrences across 24+ locations since 2026-04-22).
     *   Capturing here would not retire that volume, it would relocate it onto COO-WEBAPP at
     *   `error` level and bury the storefront's real unhandled failures underneath it. The signal
     *   TEAM-15138 relies on instead is the Datadog query
     *   `service:coo-api "requires a minimum of"`, which counts the rejections at the only place
     *   that sees all of them — including the ones this app never gets to classify.
     * - **pricing unavailable** (TEAM-14493) — the blocking `ServiceUnavailable` modal's
     *   call-the-restaurant end state for FPO code 13. chowly-api already captures every one as
     *   `Coo::PricingCalculationError`, fingerprinted by provider and provider code, so a capture
     *   here would only duplicate it without the provider detail.
     *
     * Skipping the `errorEvent ?? INIT_CHECKOUT_UNHANDLED_FAILURE` default below costs nothing for
     * any member: nothing after this block reads `errorEvent` (the first two are also classified
     * further up — `INIT_CHECKOUT_HANDLED_FAILURE` for the required-option switch case).
     */
    const isKnownDinerFacingRejection =
      isRestaurantUnavailable || requiredOptionMissing || pricingUnavailable;

    // Error event — but not for a rejection we recognise and already answer in the UI: reporting
    // one of those here as an unhandled exception is noise. See above for the members.
    if (!isKnownDinerFacingRejection) {
      errorEvent = errorEvent ?? ERROR_MESSAGES.INIT_CHECKOUT_UNHANDLED_FAILURE;
      ErrorReporter.captureException(error, 'error', {
        eventName: errorEvent,
        ...(additionalDetails && { details: additionalDetails }),
        errorEvent: errorEvent,
      });
    }

    // Never leave checkout stuck mid-initialization. Deliberately outside the guard above:
    // `restaurantUnavailable()` has already moved the status off INITIALIZING_ORDER (or left an
    // outranking ORDER_TIME_UNAVAILABLE in place), so this only re-arms the paths that would
    // otherwise strand the diner on a spinner.
    const currentStatus = (yield select(
      (s: RootState) => s.app.orderStatus.status,
    )) as OrderStatuses;
    if (currentStatus === OrderStatuses.INITIALIZING_ORDER) {
      yield put(orderStatusActions.orderPending());
    }
  }
}

/**
 * Post-initialization API calls
 *
 */
function* postInitializeOrderSaga(action: ReturnType<typeof actions.postInitializeOrder>) {
  try {
    // Fetch wanted times for the basket
    yield put(
      timeslotsActions.fetchHandoffTimesByBasket(
        action.basket.id,
        action.basket.location.id,
        determineHourType(action.fulfillmentType),
        action.basket.order_asap,
        // @ts-expect-error
        action.wantedAtDay,
        action.type,
        action.wantedAt,
      ),
    );

    // Commerce Events
    yield put(
      commerceActions.commerceCheckoutBasketStaged(
        action.basketOrder,
        action.basket,
        action.fulfillmentType,
      ),
    );

    const currentStatus = (yield select(
      (s: RootState) => s.app.orderStatus.status,
    )) as OrderStatuses;
    if (currentStatus === OrderStatuses.INITIALIZING_ORDER) {
      yield put(actions.orderPending());
    }
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      `Failed to initialize the order for basket: ${action.basket.id}.`,
      error,
    );

    // Error event
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.POST_INIT_CHECKOUT_FAILURE,
      errorResponse,
    });
  }
}

/**
 * Validate Basket
 *
 */
/**
 * The outcome of a basket validation. On failure, `surfaced` says whether this
 * function already dispatched a diner-facing state for that failure (the 502
 * recovery modal, unavailable-items, delivery- or restaurant-unavailable). Callers
 * MUST use this rather than inferring it from ambient state: `orderStatus.type` and
 * `basket.unavailableBasketItemIds` can both carry values from earlier in the session
 * (within a live basket those ids are cleared only by the diner removing the items), and
 * reading them would misjudge a fresh generic failure as already handled — leaving
 * checkout on a permanently disabled CTA (TEAM-14644 review, finding 1).
 *
 * `surfaced` alone is not enough for a caller that has to decide whether the CTA is
 * still pressable, because the failure classes surface in two different places. The 502,
 * delivery- and restaurant-unavailable classes each move `orderStatus` to a state of
 * their own; unavailable-items surfaces purely through basket state
 * (`setUnavailableBasketItems` -> the inline notice) and leaves `orderStatus` exactly as
 * the caller left it. `unavailableItemsOnly` marks that second shape so a caller mid
 * submit can settle the status itself (TEAM-14644 review, finding 3).
 *
 * **`surfaced` deliberately does NOT count the fail-closed generic message TEAM-15137 added.**
 * Both callers that read this flag treat `!surfaced` as "route this to the recovery modal", and
 * that routing is the payment-retry path — counting the message would silently replace a modal
 * with an inline sentence on the press that authorizes. It cannot go stale either, because
 * neither of them can reach the branch that dispatches it: `preAuthValidateSaga` passes
 * `rearmOnFailure`, so its generic class settles one branch earlier, and the pre-resubmit
 * re-validate in `orderSubmissionTriggerSaga` passes `settleToPending: false`, which skips the
 * whole ladder. The only caller that reaches it is `validateOrderSaga`, which ignores this
 * result. Anyone giving those two callers the generic message has to revisit their `!surfaced`
 * branches in the same change.
 *
 * **A basket coo-api answered 404 for (TEAM-15690) returns `surfaced: true`.** The validate has
 * moved `orderStatus` to BASKET_NOT_FOUND, the inline "Start a new cart" notice, which is terminal:
 * a recovery modal on top of it would retry a basket that no longer exists.
 */
type BasketValidationResult =
  | { ok: true }
  | {
      ok: false;
      surfaced: boolean;
      /**
       * True when the inline unavailable-items notice was the ONLY thing this call put in
       * front of the diner. False when a class that owns `orderStatus` also matched --
       * they are independent `if`s in the same catch and an error can carry both, and
       * settling the status on top of one of those would dismiss its blocking modal.
       */
      unavailableItemsOnly: boolean;
    };

/**
 * Settle a failed `runBasketValidation` for a caller that owns `orderStatus` while it runs (the
 * pre-auth validate, the TEAM-14644 pre-resubmit validate, and the TEAM-15341 re-price after a
 * handoff re-save). Only two failure shapes leave the status where the caller put it, and each
 * gets one dispatch; every other class has already moved `orderStatus` itself:
 * - nothing surfaced (the generic class) -> the 'validate' recovery modal;
 * - the inline unavailable-items notice was the only surface -> `orderPending()` re-arms Place
 *   Order beside it. With `itemsNeedModal` it gets the modal instead: a committed wallet's
 *   pending event is settled only by an ERROR or RECOVERY_502 status (Stripe wallet hook), so a
 *   bare `orderPending()` would leave the sheet open. The notice renders off basket state and is
 *   still there once the modal is dismissed.
 */
function* settleFailedValidation(
  validation: Extract<BasketValidationResult, { ok: false }>,
  itemsNeedModal = false,
) {
  if (!validation.surfaced || (validation.unavailableItemsOnly && itemsNeedModal)) {
    yield put(actions.recovery502Triggered('validate'));
  } else if (validation.unavailableItemsOnly) {
    yield put(actions.orderPending());
  }
}

/**
 * Validate the current basket against the ordering provider and refresh local
 * state (totals, stored value) from the response. Shared by the VALIDATE_ORDER
 * watcher and the pre-resubmit re-validation in the submit trigger (TEAM-14644).
 */
function* runBasketValidation(
  basketId: string,
  locationId: number,
  settleToPending = true,
  rearmOnFailure = false,
): Generator<unknown, BasketValidationResult> {
  const state = (yield select()) as RootState;
  const checkoutBasket = state.app.basket.checkoutBasket;
  const me = state.app.me.data;
  const guestUser = state.app.auth.guestUser;
  const updatedGuestUser = me?.id ? null : guestUser ?? DEFAULT_GUEST_USER;

  sentryLogger.info('Start basket validation from UI', {
    ...getBasketLogData(checkoutBasket),
    email: me?.email ?? updatedGuestUser?.email,
  });

  try {
    // Validate the patched checkout basket
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    yield put(basketActions.loading(true));
    const response: SagaReturnType<typeof validateBasket> = yield* retryOnceOnStatuses(
      validateBasket,
      { statuses: GATEWAY_STATUSES },
      { basketId, locationId, guestUser: updatedGuestUser },
      { client },
    );

    sentryLogger.info('Basket validation from UI success', {
      ...getBasketLogData(response),
      email: me?.email ?? updatedGuestUser?.email,
    });

    yield put(basketActions.success(response));
    // TEAM-14787: a successful validate is what prices the basket.
    yield put(actions.basketPricingSettled(basketId, 'priced'));

    if (response.gift_card_amount_authorized) {
      yield put(
        paymentActions.storedValueSet({
          balance: response.gift_card_amount ?? 0,
          amount_authorized: response.gift_card_amount_authorized ?? 0,
          remaining_balance: response.gift_card_remaining_balance ?? 0,
          card_number: response.gift_card_number ?? '',
        }),
      );
    }

    // Success. When invoked as a pre-resubmit step (`settleToPending = false`) keep the
    // transmitting state that ORDER_SUBMISSION_TRIGGER set, so Place Order stays
    // disabled through the resubmit that immediately follows. Flipping to pending here
    // would briefly re-enable the button mid-submit and open a double-submit window
    // that the first attempt does not have (TEAM-14644).
    if (settleToPending) {
      yield put(actions.orderPending());
    }
    return { ok: true };
  } catch (error) {
    // TEAM-14787: see `IOrderStatusState.basketPricing`. Skipped when the checkout has moved on
    // to another basket (a restart or store switch) while this validate was in flight: its failure
    // says nothing about the current basket and would replace that basket's verdict.
    const currentBasketId = ((yield select()) as RootState).app.basket.checkoutBasket?.id;
    if (!currentBasketId || currentBasketId === basketId) {
      yield put(actions.basketPricingSettled(basketId, 'failed'));
    }

    sentryLogger.error('Basket Validation from UI failed', {
      ...getBasketLogData(checkoutBasket),
      email: me?.email ?? updatedGuestUser?.email,
      error,
    });

    // TEAM-15690: coo-api has lost this basket. Ahead of the recovery modal and the generic ladder
    // below, both of which offer a retry against the same basket id. `surfaced: true`, so no caller
    // opens the 'validate' recovery modal on top of the notice (`settleFailedValidation`), and the
    // BASKET_VALIDATION_ERROR capture below is skipped: `surfaceBasketNotFound` reports it.
    if (yield* surfaceBasketNotFound(error, { basketId, locationId, source: 'validate' })) {
      return { ok: false, surfaced: true, unavailableItemsOnly: false };
    }

    /**
     * TEAM-14320: widened past the original 502-only gate. A browser transport failure
     * (`TypeError: Failed to fetch`) carries no `.status`, and a 200-with-HTML page
     * surfaces from the SDK's content-type middleware as a `NonJsonResponseError` whose
     * `.status` is 200 — both used to miss this gate, fall through to
     * `prepareErrorMessage` + `captureException`, and show the diner nothing while the
     * reducer left checkout on `type: UPDATING` (a permanently disabled
     * "Order updating...." CTA).
     *
     * Deliberately NOT `isRetryableError`: that would also pull in 408/429/500/503/504,
     * which TEAM-14612 kept off the validate path on purpose. A 500-with-HTML still falls
     * through — out of scope here, not an oversight.
     *
     * This widens only which failures OPEN the recovery modal. The
     * `retryOnceOnStatuses(..., { statuses: GATEWAY_STATUSES })` above is untouched:
     * `validateBasket` is a POST, and docs/COO_API_FAILURE_HANDLING.md's "Never retry
     * mutations" rule keeps every retry here diner-initiated (TEAM-13878).
     */
    if (opensValidateRecovery(error)) {
      yield put(actions.recovery502Triggered('validate'));
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.BASKET_VALIDATION_ERROR,
      });
      // RECOVERY_502_TRIGGERED owns `orderStatus` from here (it sets ORDER_PENDING
      // behind the modal), so the caller must not settle the status on top of it.
      return { ok: false, surfaced: true, unavailableItemsOnly: false };
    }

    // Error Notification
    //
    // This sentence is tier 4 of the render rule (docs/DINER_ERROR_MESSAGES.md) AND the line this
    // catch reports to Sentry — it names a basket id, so it reads as a log entry, not as diner
    // copy. It is bound to a const because the generic surface below has to tell "the envelope
    // carried copy" from "the render rule fell through to ours" (TEAM-15137).
    const validationFallbackMessage = `There was an issue validating your order for the following basket: ${basketId}.`;
    const errorResponse = (yield call(
      prepareErrorMessage,
      validationFallbackMessage,
      error,
    )) as SagaReturnType<typeof prepareErrorMessage>;
    // Track what this call actually put in front of the diner, so the caller does not
    // have to guess it from state that may predate this validation. Tracked as two
    // separate flags because the classes surface in two different places: `itemsSurfaced`
    // shows a notice rendered off BASKET state and leaves `orderStatus` untouched, while
    // `statusSurfaced` means a class has already moved `orderStatus` into a state of its
    // own that must not be overwritten.
    let itemsSurfaced = false;
    let statusSurfaced = false;

    const unavailableBasketItemIds = getUnavailableBasketItemIds(errorResponse);
    if (unavailableBasketItemIds.length) {
      yield put(
        basketActions.setUnavailableBasketItems(
          unavailableBasketItemIds,
          errorResponse.error?.error,
        ),
      );
      itemsSurfaced = true;
    }

    // TEAM-14493: checked FIRST, and it excludes the two text-matched classes below. A pricing
    // failure (FPO code 13) is terminal, and its envelope carries chowly-api's generic fallback
    // `error_description`, so it must never also open the delivery- or restaurant-unavailable
    // modal. Setting `statusSurfaced` is what keeps the Retry recovery modal shut for the two
    // pre-submit callers (`preAuthValidateSaga`, the pre-resubmit re-validate), which open it only
    // on `!surfaced`, and it skips the generic status-bar copy at the foot of this ladder.
    const pricingUnavailable = isPricingUnavailableError(errorResponse);
    const deliveryUnavailable = !pricingUnavailable && isDeliveryUnavailableError(errorResponse);
    const restaurantUnavailable =
      !pricingUnavailable && isRestaurantUnavailableError(errorResponse);

    if (pricingUnavailable) {
      yield put(actions.pricingUnavailable());
      statusSurfaced = true;
    }

    if (deliveryUnavailable) {
      yield put(actions.deliveryUnavailable());
      statusSurfaced = true;
    }

    if (restaurantUnavailable) {
      yield put(actions.restaurantUnavailable(getRestaurantUnavailableReason(errorResponse)));
      statusSurfaced = true;
    }

    // TEAM-14292: surface the email error as an inline message. Uses orderStatusMessageErrorSet
    // (not orderSubmissionError) because validate runs before payment — there is no submission to
    // fail. The independent `if` is intentional: other conditions above (delivery/restaurant
    // unavailable) are not mutually exclusive with the email check in this path.
    //
    // TEAM-14292 run 4: if coo-api sends `user_message` on the 400 (e.g. "Did you mean
    // bnarina@gmail.com?"), surface it so per-reason copy reaches the diner without an FE
    // release. `INVALID_EMAIL_USER_MESSAGE` is the fallback when coo-api sends no
    // `user_message`. We read `user_message` off the already-parsed envelope rather than
    // calling `prepareErrorMessage` a second time because the SDK throws the raw `Response`,
    // and its body is already consumed by the first call — a second `.text()` would reject.
    const invalidEmail = isInvalidEmailError(error, errorResponse);
    if (invalidEmail) {
      // TEAM-14292: settle status to ORDER_PENDING so the CTA re-arms — but only when
      // no terminal status (delivery/restaurant unavailable) has already been surfaced.
      // Dispatching orderPending() first (which sets message: null) is safe because
      // orderStatusMessageErrorSet immediately overwrites message and type below.
      if (!statusSurfaced) {
        yield put(actions.orderPending());
      }
      yield put(
        actions.orderStatusMessageErrorSet(
          errorResponse.error?.user_message || INVALID_EMAIL_USER_MESSAGE,
          {
            href: ROUTES.ACCOUNT,
            label: 'Go to Account Settings',
          },
        ),
      );
    }

    /**
     * TEAM-15137: the diner-facing sentence for a failure none of the classes above claimed, or
     * `null` when there is none worth showing. Resolved, not authored — see the branch that
     * renders it at the foot of the ladder below for why it is gated this way.
     */
    const genericDinerCopy =
      !itemsSurfaced && errorResponse.message !== validationFallbackMessage
        ? errorResponse.message
        : null;

    // TEAM-14818: a caller that opted into rearmOnFailure (the tip re-validate) must not leave the
    // diner stuck on "Order updating...." after a generic refresh failure — but it must NOT re-arm
    // when the validate surfaced a terminal/blocking condition. delivery/restaurant-unavailable
    // route to their own modals, 502s took the recovery path above, an unavailable-items result
    // (setUnavailableBasketItems, dispatched just above) must keep Place Order disabled so the diner
    // cannot submit a basket carrying items the validate just flagged, and an invalid-email error
    // must remain visible (TEAM-14292). The default (fail closed, TEAM-14666) is unchanged for
    // every caller that does not opt in.
    //
    // TEAM-14696: "fail closed" has to mean "Place Order stays disabled", not "the basket is
    // still being written". `isBasketMutating` reads `type`, so leaving it at UPDATING latches
    // basketMutationGuard — every control disables and every queued write waits out the 30s
    // escape, and the unavailable-items class excluded from the re-arm above is the path that
    // hits it most (it also defers the `wantedAtBasketSet` that removing those items
    // dispatches). `orderStatusMessageClear()` sets `type: OK` while preserving `status`
    // (reducer.ts), and Place Order is gated on `status === ORDER_PENDING`
    // (submitOrder/index.tsx), so the submit stays closed while the guard and the queue release
    // immediately. It is the same action the unavailable-items notices already dispatch when the
    // diner acts on them.
    //
    // The release is gated on `settleToPending` for the same reason the success path is: the
    // pre-resubmit caller (TEAM-14644) passes false because ORDER_SUBMISSION_TRIGGER's
    // TRANSMITTING state is its to own, and it settles every failure class itself off the
    // returned flags. Clearing here would flip `type` to OK mid-submit — the double-submit
    // window `settleToPending` exists to keep shut. Nothing latches on that path either: each
    // of its branches ends on RECOVERY_502 / ERROR / ORDER_PENDING, all of which read as idle.
    if (statusSurfaced || invalidEmail) {
      // statusSurfaced: `pricingUnavailable()` / `deliveryUnavailable()` /
      // `restaurantUnavailable()` already moved `type` to ERROR, and clearing on top would
      // dismiss a modal that is terminal by design.
      // The 502 class returned above under RECOVERY_502 and never reaches here.
      //
      // invalidEmail: `orderPending()` + `orderStatusMessageErrorSet` (dispatched above)
      // already set status: ORDER_PENDING, type: ERROR, and the message. No further settle
      // needed.
    } else if (rearmOnFailure && !itemsSurfaced) {
      yield put(actions.orderPending());
    } else if (settleToPending) {
      // TEAM-15137: the class nothing above recognised used to settle SILENTLY — the diner was
      // left with a disabled Place Order and no sentence anywhere on the page, while coo-api had
      // already authored one. `option_not_on_menu` is the case: `error_data: []` matches none of
      // the classes above, and its copy resolves at tier 3 from the envelope's `user_message`
      // (docs/DINER_ERROR_MESSAGES.md). `errorResponse.message` IS that resolved copy — the render
      // rule has already been applied, once, by the `prepareErrorMessage` call above. Nothing new
      // is authored here; this only renders what the backend owns.
      //
      // Gated on the resolution, not on the slug. When nothing in the envelope resolves, the rule
      // returns this call site's own `validationFallbackMessage` — a log line carrying a basket id
      // — and putting that on screen would be a worse outcome than the silence it replaces. So the
      // identity check is the gate, and a copy-less envelope keeps exactly today's behaviour.
      // (Same shape as `UNEXPLANATORY_MESSAGES` in errorReconciliation/index.tsx: the surface
      // decides whether the resolved sentence actually explains anything.) Re-resolving with a
      // different fallback is not an option — the rule is applied once per error by design, and a
      // second pass re-fires its multiple-codes warning.
      //
      // `itemsSurfaced` is excluded: the inline notice already names the affected items, and an
      // unavailable-items envelope can still carry an envelope-level `user_message` (the dedicated-
      // UI suppression in `resolveDinerMessage` covers the FPO half of tier 3, not that half), so
      // without this the diner would read the same failure twice on one screen.
      //
      // Both branches keep the ticket-documented state contract, and for the same reason: neither
      // touches `status`, so Place Order stays gated (TEAM-14666), and both move `type` off
      // UPDATING — OK for the clear, ERROR for the message (reducer.ts) — so `basketMutationGuard`
      // releases instead of latching (TEAM-14696). The status bar renders the message with a
      // dismiss control that dispatches the same clear, which is again type-only.
      //
      // Deliberately the status bar rather than the global toast's "Refresh Page" affordance that
      // `postAddItemSaga` pairs with this same slug (basket/saga.ts): there the diner is picking an
      // item and a reload re-fetches the menu they are about to choose from, but here the option is
      // already IN the basket and a reload re-fetches the same basket with it. The recovery is
      // editing the cart, which checkout already offers. Whether coo-api's wording should change
      // for the validate route is a copy decision, and is deliberately not made here.
      if (genericDinerCopy) {
        yield put(actions.orderStatusMessageErrorSet(genericDinerCopy));
      } else {
        yield put(actions.orderStatusMessageClear());
      }
    }

    // Error event — but not for a pricing failure (code 13): chowly-api already captures every one
    // as `Coo::PricingCalculationError`, so a capture here would only duplicate it without the
    // provider detail. Same reasoning as the init path's `isKnownDinerFacingRejection` (TEAM-16128).
    if (!pricingUnavailable) {
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.BASKET_VALIDATION_ERROR,
        errorResponse,
      });
    }
    return {
      ok: false,
      surfaced: itemsSurfaced || statusSurfaced || invalidEmail,
      unavailableItemsOnly: itemsSurfaced && !statusSurfaced,
    };
  } finally {
    yield put(basketActions.loading(false));
  }
}

/**
 * The Toast payment intent the applied card was collected against, or undefined off Toast.
 * coo-api mints a new one whenever the payable total changes, so comparing it across a
 * validate is how `preAuthValidateSaga` tells "the basket refreshed and the card still works"
 * from "the basket refreshed and the card is now useless" (TEAM-14644 UAT follow-up).
 */
function* selectRootState(): Generator<unknown, RootState> {
  return (yield select()) as RootState;
}

function* selectToastIntentId(): Generator<unknown, string | undefined> {
  const state = (yield select()) as RootState;
  return state.app.basket.checkoutBasket?.payment_meta?.payment_intent_id;
}

/**
 * TEAM-14644 (UAT follow-up): the pre-authorization re-validation a retry press runs before
 * it lets a payment flow commit an amount. Used by the Toast submit button, whose
 * `toastPaymentConfirmed()` lands before ORDER_SUBMISSION_TRIGGER is ever dispatched, so the
 * pre-resubmit gate in `orderSubmissionTriggerSaga` can never see it un-authorized.
 *
 * Two differences from the VALIDATE_ORDER watcher, and both matter:
 *
 *  - `rearmOnFailure` is ON, as belt-and-braces. Both branches below already settle every
 *    failure class this caller can reach — the generic one via `recovery502Triggered`, which
 *    sets ORDER_PENDING itself, and the unavailable-items one via `orderPending()` — so the
 *    flag changes no observable state today. It stays because a caller that holds the CTA
 *    down waiting on this must never depend on those two branches remaining exhaustive
 *    (TEAM-14818); without it a class that stops being settled strands the diner on
 *    "Order updating...." forever.
 *  - the outcome is published as state (`preAuthValidation`) instead of being left for the
 *    caller to infer. Inference does not work here: with `rearmOnFailure` on, a generic
 *    failure settles on the same ORDER_PENDING/OK a success does, so a caller reading
 *    status/type would authorize against a basket this validate could not refresh — which is
 *    strictly worse than the bug being fixed.
 *
 * WHY RE-ARMING IS SAFE HERE, given docs/CHECKOUT_PAGE.md "Failed validates fail CLOSED".
 * This is the first caller that re-arms Place Order on the submit press itself after a failed
 * validate while a Toast payment intent exists (the gift-card remove and tip re-validates also
 * pass `rearmOnFailure`, but neither is the press that authorizes), so it looks like the TEAM-14043 behaviour TEAM-14666 reverted. It is
 * not, but NOT for the reason the pre-resubmit branch in `orderSubmissionTriggerSaga` gives —
 * that branch argues "no intent exists once nothing is authorized", and on Toast the intent
 * exists from iframe init, it is merely unconfirmed. What makes it safe here is a different
 * invariant: `submitFailed` is cleared ONLY by INITIALIZE_ORDER / DESTROY_ORDER /
 * ORDER_NOT_INITIALIZED — never by any failure path, and notably not by the 502 modal's retry
 * (checkoutRecoveryError.tsx dispatches forceOrderPending + validateOrder and leaves it set).
 * So the CTA coming back does not mean the next press can authorize: every press re-enters
 * `preAuthValidate` and authorization requires a fresh 'succeeded'. Anyone who makes a failure
 * path clear `submitFailed` breaks that, and re-arming here becomes unsafe.
 */
export function* preAuthValidateSaga(action: ReturnType<typeof actions.preAuthValidate>) {
  let verdict: Exclude<PreAuthValidation, 'pending'> = 'failed';
  try {
    const intentBefore = yield* selectToastIntentId();

    // TEAM-15341 (AC3/AC4): a handoff mismatch is re-saved HERE, before authorization, which
    // is the only point the Toast path can do it safely (after `toastPaymentConfirmed()` the
    // intent is fixed at the old total). No unchanged-total requirement: nothing is authorized
    // yet, and a total the re-save moves is caught below the same way any moved total is —
    // coo-api mints a new intent, `intentRotated`, re-enter payment. A failed re-save has
    // already opened the conveyance recovery modal; the verdict stays 'failed' (published in
    // the finally) so the caller authorizes nothing. `intentBefore` is read before the re-save
    // on purpose, so a rotation the re-save itself causes still counts.
    const stateBeforeResave = yield* selectRootState();
    const reconciled = yield* reconcileConveyanceBeforeSubmit(
      stateBeforeResave,
      /* requireUnchangedTotal */ false,
      // The validate right below re-prices; doing it in the helper too would validate twice.
      /* repriceAfterResave */ false,
    );
    if (reconciled === 'recovery') return;

    const validation = yield* runBasketValidation(
      action.basketId,
      action.locationId,
      /* settleToPending */ true,
      /* rearmOnFailure */ true,
    );

    // Whether the payable total MOVED, expressed the only way that is actually load-bearing:
    // coo-api mints a new Toast payment intent whenever it does. Measured on store 16801 —
    // one validate that took the total 7300 -> 7500 also took payment_intent_id
    // 3a222c81... -> 34505b30... The card the diner applied was collected against the old
    // intent, `useToast` reinitialises the iframe off the new transaction_id, and the card is
    // gone. Nothing can be confirmed, so this press cannot submit.
    //
    // Decided HERE rather than in the component: the component would have to read
    // `payment.toast.created` from whichever render the verdict happens to land in, and that
    // races the reinit effects — observed live, the component sees the card as still applied
    // and walks into `toastSubmitOrder`'s early return instead. The saga reads the store at
    // two fixed points and is not subject to render ordering.
    const intentAfter = yield* selectToastIntentId();
    // Fail CLOSED on a missing id: "had an intent, now there is none" is a rotation as far as
    // the applied card is concerned, and treating it as 'succeeded' would send the caller on
    // to authorize against an intent that may no longer exist. Only an unchanged, present id
    // counts as safe.
    const intentRotated = Boolean(intentBefore) && intentBefore !== intentAfter;

    verdict = !validation.ok ? 'failed' : intentRotated ? 'intent_rotated' : 'succeeded';

    if (!validation.ok) {
      // Settled by `settleFailedValidation`.
      //
      // The generic class: `runBasketValidation` built a diner-facing error and dispatched
      // nothing with it. `rearmOnFailure` gives the CTA back but says nothing, so without
      // this the diner presses, waits, and gets a live button and silence. Route it to the
      // recovery modal the sibling path already uses for this exact class
      // (orderSubmissionTriggerSaga), whose `validate` copy — cart saved, card not charged —
      // is literally accurate here: nothing has been authorized at this point.
      //
      // Unavailable items: the inline notice renders off BASKET state and never touches
      // `orderStatus`, and `rearmOnFailure` deliberately skips this class — so without the
      // `orderPending()` the status stays VALIDATING_ORDER and the diner reads "some items are
      // unavailable" above a permanently disabled CTA. Same reason the pre-resubmit branch in
      // `orderSubmissionTriggerSaga` pairs the notice with `orderPending()`.
      yield* settleFailedValidation(validation);
    }
  } finally {
    // ALWAYS publish a verdict, including when `runBasketValidation` throws out of its own
    // catch (it calls prepareErrorMessage / getUnavailableBasketItemIds and several puts,
    // none of them fenced). A caller is holding the CTA down waiting on this: without the
    // finally, `preAuthValidation` would stay 'pending' forever and Place Order would be
    // dead until a page reload. Defaults to 'failed', so a throw can never authorize.
    yield put(actions.preAuthValidateSettled(verdict));
  }
}

export function* validateOrderSaga(action: ReturnType<typeof actions.validateOrder>) {
  // settleToPending stays the default for this watcher; rearmOnFailure is forwarded so the
  // TEAM-14818 opt-in survives the extraction (it defaults false, so TEAM-14666's fail-closed
  // behavior is unchanged for every caller that does not opt in).
  yield* runBasketValidation(action.basketId, action.locationId, true, action.rearmOnFailure);
}

/**
 * Add Tip
 */
export function* tipAddSaga(action: ReturnType<typeof actions.tipAdd>) {
  const state = (yield select()) as RootState;
  const checkoutBasket = state.app.basket.checkoutBasket;

  try {
    yield put(
      basketActions.success({
        ...checkoutBasket,
        tip: action.tipAmount,
      } as Basket),
    );
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const { basketId, locationId } = getIdsFromState(state);
    const response = (yield call(
      addTip,
      { basketId, locationId, tipAmount: action.tipAmount },
      { client },
    )) as SagaReturnType<typeof addTip>;

    // Success
    yield put(actions.orderPending());
    yield put(basketActions.success(response));

    // TEAM-14818: re-validate so the gift-card authorized amount refreshes to include the new tip
    // (chowly-api#2575 folds tip + self-delivery fee into amount_authorized; coo-api otherwise
    // rejects the submit with "Gift card stored value does not match"). Only when a gift card is
    // applied. VALIDATE_ORDER holds the submit control DISABLED (VALIDATING_ORDER) for the whole
    // in-flight refresh, so the diner cannot submit the stale authorized amount mid-refresh — we
    // deliberately do NOT re-arm ORDER_PENDING here. validateOrderSaga re-arms it on success (with
    // the fresh amount); passing rearmOnFailure re-arms it after a generic refresh failure too, so a
    // failed refresh never strands the diner on "Order updating...." (never re-opening the
    // mid-refresh window TEAM-14666 guards). Mirrors the non-fatal re-validate pattern TEAM-13915
    // (PR #783, separate) introduces on the gift-card apply/remove paths.
    //
    // TEAM-14493: skipped while checkout sits on the PRICING_UNAVAILABLE modal. This tip is then the
    // default-tip auto-apply behind the modal, and the `orderPending()` above was refused, so status
    // is still PRICING_UNAVAILABLE. VALIDATE_ORDER is one of the guard's allowed exits: dispatching
    // it closed the modal for the length of the request (a flicker), and an unclassified failure
    // then took `rearmOnFailure`'s `orderPending()` — Place Order live with no modal. Nothing can be
    // submitted from that state, and every way out of it (a fresh init or validate) refreshes the
    // authorized amount itself. Not skipped behind RESTAURANT_UNAVAILABLE: its reducer guard
    // (TEAM-16128) refuses the `orderPending()` above, but this re-validate still goes out and
    // its VALIDATE_ORDER exits the modal (TEAM-15220).
    //
    // TEAM-15690: likewise behind the BASKET_NOT_FOUND notice. Its reducer guard refuses the
    // VALIDATE_ORDER, but the validate itself would still go out and 404 against a basket coo-api
    // no longer has.
    const statusAfterTip = (yield select(
      (s: RootState) => s.app.orderStatus.status,
    )) as OrderStatuses;
    if (
      state.app.payment.storedValue?.amount_authorized &&
      statusAfterTip !== OrderStatuses.PRICING_UNAVAILABLE &&
      statusAfterTip !== OrderStatuses.BASKET_NOT_FOUND
    ) {
      yield put(actions.validateOrder(basketId, locationId, true));
    }
  } catch (error) {
    yield put(
      basketActions.success({
        ...checkoutBasket,
        tip: checkoutBasket.tip,
      } as Basket),
    );

    // TEAM-14642: parse the refusal BEFORE deciding whether to re-arm Place Order.
    // This block used to dispatch orderPending() unconditionally, above and before
    // the error was inspected at all — so a tip refused because the restaurant had
    // stopped accepting orders handed the diner back an enabled Place Order button
    // that was guaranteed to fail.
    const errorResponse = (yield call(
      prepareErrorMessage,
      OrderStatusMessages.TIP_ADDING_ERROR,
      error,
    )) as SagaReturnType<typeof prepareErrorMessage>;

    if (isRestaurantUnavailableError(errorResponse)) {
      // Terminal for this checkout: apply the blocking state and leave the submit
      // control disabled. Deliberately NOT followed by orderPending() — the same
      // fail-closed reasoning TEAM-14666 locked in on the validate path.
      yield put(actions.restaurantUnavailable(getRestaurantUnavailableReason(errorResponse)));
    } else {
      yield put(actions.orderPending());
      yield put(actions.orderStatusMessageErrorSet(errorResponse.message));
    }

    // Error event
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.ADD_TIP_ERROR,
      errorResponse,
    });
  }
}

/**
 * Set Utensils
 *
 */
export function* utensilsSetSaga(action: ReturnType<typeof actions.utensilsSet>) {
  try {
    const state = (yield select()) as RootState;
    const location = state.app.basket.location;
    if (!location.supports_utensils) {
      yield put(orderStatusActions.orderPending());
      return;
    }
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const { basketId, locationId } = getIdsFromState(state);
    if (!basketId || !locationId) {
      // Same reason as the `!supports_utensils` return above: UTENSILS_SET has
      // already flipped state to UPDATING / UTENSILS_SETTING, and bailing without
      // clearing it latches basketMutationGuard until the 30s escape (TEAM-14696).
      yield put(orderStatusActions.orderPending());
      return;
    }
    const response: SagaReturnType<typeof setUtensils> = yield call(
      setUtensils,
      { basketId, locationId, preference: action.utensils },
      { client },
    );

    // Success
    yield put(actions.orderPending());
    yield put(basketActions.success(response));

    genericEventHandler(GlobalEvents.GENERIC__CTA, {
      name: EventNames.UTENSILS_SET,
      details: action.utensils ? 'Added' : 'Removed',
    });

    genericEventHandler(GlobalEvents.BASKET__CUSTOMIZED, {
      name: EventNames.UTENSILS_SET,
      details: action.utensils ? 'Added' : 'Removed',
    });
  } catch (error) {
    yield put(actions.orderPending());

    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      OrderStatusMessages.UTENSIL_SETTING_ERROR,
      error,
    );
    yield put(actions.orderStatusMessageErrorSet(errorResponse.message));

    // Error event
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.UTENSILS_ERROR,
      errorResponse,
    });
  }
}

/**
 * Set Basket Instructions
 *
 */
export function* basketInstructionsSetSaga(
  action: ReturnType<typeof actions.basketInstructionsSet>,
) {
  try {
    const state = (yield select()) as RootState;
    const { basketId, locationId } = getIdsFromState(state);
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const response: SagaReturnType<typeof setInstructions> = yield call(
      setInstructions,
      { basketId, locationId, basket_instructions: action.basketInstructions.instructions },
      { client },
    );

    // Success
    yield put(actions.orderPending());
    yield put(basketActions.success(response));
  } catch (error) {
    yield put(actions.orderPending());

    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      OrderStatusMessages.BASKET_INSTRUCTIONS_SETTING_ERROR,
      error,
    );
    yield put(actions.orderStatusMessageErrorSet(errorResponse.message));

    // Error event
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.BASKET_INSTRUCTIONS_ERROR,
      errorResponse,
    });
  }
}

export function* orderSubmissionDigitalWalletTriggerSaga(
  action: ReturnType<typeof actions.orderDigitalWalletTrigger>,
) {
  const client = createHttpClient({
    origin: getOrigin(window.location.host),
  });
  const state = (yield select()) as RootState;
  const conveyanceMode = state.app.conveyanceMode;
  const { basketId, locationId } = getIdsFromState(state);

  /**
   * TEAM-15341: no basket means there is nothing to pay for — almost always a late wallet
   * `token` arriving after the order already went through (`destroyOrder()` clears the
   * basket on success). Stop here, before `creditCardSet` (which fires COO-WEBAPP-494 from the
   * commerce saga against the torn-down basket) and before the submit chain (which threw the
   * "handoff mode" error, COO-WEBAPP-3XN). The Stripe hook already drops these at the
   * listener; this covers every wallet dispatcher (Stripe, Square, Olo Pay).
   *
   * `orderNotInitialized()` undoes the TRANSMITTING status the dispatcher set and restores the
   * truthful one: with no basket there is no initialized order. It must not be
   * `orderPending()` — the cart's Checkout button only re-initializes from
   * ORDER_NOT_INITIALIZED (cart/footer.tsx), so ORDER_PENDING here stranded the diner's NEXT
   * order in the same tab on a checkout with no basket (caught in the TEAM-15341 browser run).
   */
  if (!state.app.basket.checkoutBasket) {
    sentryLogger.warn('Digital wallet submit ignored: no checkout basket', {
      status: state.app.orderStatus.status,
      selectedDigitalWallet: state.app.payment.selectedDigitalWallet,
    });
    yield put(actions.orderNotInitialized());
    return;
  }

  const deliveryAddress = assembleDeliveryObjectFromState(state);

  try {
    yield put(paymentActions.creditCardSet(action.paymentDetails));

    // Update delivery address phone number
    if (
      deliveryAddress?.street_address &&
      conveyanceMode.type === CONVEYANCE_TYPES.DELIVERY &&
      deliveryAddress?.phone_number !== conveyanceMode.address?.phone_number
    ) {
      const updatedBasket: Basket = yield* retryOnceOnStatuses(
        setConveyance,
        { statuses: GATEWAY_STATUSES },
        {
          basketId,
          locationId,
          type: API_CONVEYANCE_TYPES.DELIVERY,
          data: deliveryAddress ? convertConveyanceToDeliveryAddress(deliveryAddress) : undefined,
        },
        { client },
      );
      yield put(basketActions.success(updatedBasket));
    }

    yield put(actions.orderSubmissionTrigger(action.captcha, action.queryClient));
  } catch (err) {
    const error = err as Error;

    // Surface the recoverable 502 modal (cart preserved) instead of the
    // terminal "error setting your handoff mode" dead end at submit.
    if (isStatusError(error, GATEWAY_STATUSES)) {
      yield put(actions.recovery502Triggered('conveyance'));
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.SET_CONVEYANCE_ERROR,
      });
      return;
    }

    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.SET_CONVEYANCE_ERROR,
    });
    yield put(actions.orderSubmissionError(error.message));
  }
}

/**
 * The setConveyance args that re-save the diner's handoff choice, or `null` when it cannot be
 * re-saved: no basket/location ids, a delivery address without a street or phone, or a handoff
 * type other than pickup and delivery (the only two this app writes, TEAM-14383).
 */
const getConveyanceResaveArgs = (state: RootState): Parameters<typeof setConveyance>[0] | null => {
  const { basketId, locationId } = getIdsFromState(state);
  if (!basketId || !locationId) return null;

  switch (state.app.conveyanceMode?.type) {
    case CONVEYANCE_TYPES.PICKUP:
      return { basketId, locationId, type: API_CONVEYANCE_TYPES.PICKUP };
    case CONVEYANCE_TYPES.DELIVERY: {
      const deliveryAddress = assembleDeliveryObjectFromState(state);
      if (!deliveryAddress?.street_address || !deliveryAddress?.phone_number) return null;
      return {
        basketId,
        locationId,
        type: API_CONVEYANCE_TYPES.DELIVERY,
        data: convertConveyanceToDeliveryAddress(deliveryAddress),
      };
    }
    default:
      return null;
  }
};

/**
 * TEAM-15341 (AC3/AC4): when the checkout basket's saved handoff disagrees with the diner's
 * handoff choice at Place Order, re-save the choice once (a gateway 502 is retried once, as at
 * every other setConveyance site), re-price it (`repriceAfterResave`), and continue with the
 * refreshed basket. If it cannot be re-saved, or still disagrees, show the cart-preserving
 * conveyance recovery modal (Try Again) instead of the terminal "refresh this page" error.
 *
 * The re-price is required because coo-api's setConveyance does not re-price (the Chowly engine
 * returns the stored basket with only the handoff changed), so the re-save response's `total` is
 * the pre-re-save one. Only a validate writes `total` / `delivery_fee`. `preAuthValidateSaga`
 * passes false because it validates right after this; the submit trigger passes true. A failed
 * re-price never submits.
 *
 * Boundaries, each load-bearing:
 * - Never without a basket. `no_basket` is left to `assembleOrderDataFromState`'s throw: there
 *   is nothing to re-save onto, and a late wallet token on a torn-down basket must never reach a
 *   write or a submit.
 * - Toast re-saves BEFORE authorization, from `preAuthValidateSaga` (the submit button sends a
 *   mismatched press there even on a first press). After `toastPaymentConfirmed` the intent is
 *   fixed at the authorized total, so `orderSubmissionTriggerSaga` never re-saves then: a
 *   mismatch that still gets there opens the recovery modal instead (belt and braces).
 * - A committed wallet amount (`requireUnchangedTotal`) IS re-saved — every observed diner who
 *   hit a real mismatch did so through the Stripe wallet path — but the submit continues only if
 *   the re-priced `checkoutBasket.total` strictly equals the total before the re-save. That is
 *   the amount the diner approved: the sheet is built from `checkoutBasket.total`
 *   (`getDigitalWalletsOptions`, via `assemblePaymentWithoutCustomer`). Any change goes to the
 *   recovery modal; RECOVERY_502 then makes the wallet hook complete the pending event with
 *   'fail' (TEAM-14307), the sheet closes, and Try Again opens a new sheet at the new total.
 *   A total the diner did not approve is never submitted.
 * - Uncommitted payments (saved or new card, pay in store) re-save and continue: they are
 *   charged at submit, against whatever total is submitted.
 * - Only pickup and delivery: the only two types this app ever writes with setConveyance (and
 *   the only two the Chowly engine processes, TEAM-14383). Any other mismatch goes to the modal.
 * - No status action is dispatched on the success path. Both callers run it while a status
 *   that disables Place Order is already set (ORDER_SUBMISSION_TRIGGER -> TRANSMITTING,
 *   PRE_AUTH_VALIDATE -> UPDATING), and basketMutationGuard defers every other basket write
 *   until it settles, so the re-save cannot race one, and the submit that follows is still
 *   the only one.
 */
function* reconcileConveyanceBeforeSubmit(
  state: RootState,
  requireUnchangedTotal: boolean,
  repriceAfterResave: boolean,
): Generator<unknown, 'in_sync' | 'resaved' | 'recovery'> {
  if (getConveyanceCheck(state) !== 'mismatch') return 'in_sync';

  const resaveArgs = getConveyanceResaveArgs(state);

  const logData = {
    ...getBasketLogData(state.app.basket.checkoutBasket),
    conveyanceModeType: state.app.conveyanceMode?.type,
    basketConveyanceType: state.app.basket.checkoutBasket?.conveyance_type?.type,
  };

  if (!resaveArgs) {
    sentryLogger.warn('Handoff mismatch at submit could not be re-saved', {
      ...logData,
      canResave: false,
    });
    yield put(actions.recovery502Triggered('conveyance'));
    return 'recovery';
  }

  // The try covers the re-save alone, so only a failed setConveyance is reported (and shown) as one.
  try {
    const client = createHttpClient({ origin: getOrigin(window.location.host) });
    const basket = yield* retryOnceOnStatuses(
      setConveyance,
      { statuses: GATEWAY_STATUSES },
      resaveArgs,
      { client },
    );
    yield put(basketActions.success(basket));
    // Keep `basket.fulfillment` in step with the server basket, as conveyanceSetSaga does.
    // Left stale, a later basket sync re-derives `conveyanceMode` from it and recreates the
    // very mismatch this just repaired (and, after a recovery, the next press would re-save
    // the old handoff back). `wanted_at` comes from the pre-re-save snapshot.
    yield put(
      basketActions.updateBasketFulfillment(
        buildFulfillmentFromBasket(basket, state.app.basket.checkoutBasket?.wanted_at),
      ),
    );
  } catch (error) {
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.SET_CONVEYANCE_ERROR,
      ...logData,
    });
    sentryLogger.warn('Handoff mismatch at submit could not be re-saved', {
      ...logData,
      canResave: true,
    });
    yield put(actions.recovery502Triggered('conveyance'));
    return 'recovery';
  }

  if (getConveyanceCheck((yield select()) as RootState) !== 'match') {
    sentryLogger.warn('Handoff re-save did not resolve the mismatch', logData);
    yield put(actions.recovery502Triggered('conveyance'));
    return 'recovery';
  }

  // setConveyance does not re-price (see above), so re-price before deciding, as the TEAM-14644
  // retry does. The handoff is saved by now: any failure here is a validate failure, never the
  // 'conveyance' recovery, including a throw out of `runBasketValidation`'s own (unfenced) catch.
  if (repriceAfterResave) {
    let validation: BasketValidationResult;
    try {
      validation = yield* runBasketValidation(
        resaveArgs.basketId,
        resaveArgs.locationId,
        /* settleToPending */ false,
      );
    } catch (error) {
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.BASKET_VALIDATION_ERROR,
        ...logData,
      });
      yield put(actions.recovery502Triggered('validate'));
      return 'recovery';
    }
    if (!validation.ok) {
      sentryLogger.warn('Re-pricing after the handoff re-save failed', logData);
      yield* settleFailedValidation(validation, /* itemsNeedModal */ requireUnchangedTotal);
      return 'recovery';
    }
  }

  if (requireUnchangedTotal) {
    const approvedTotal = state.app.basket.checkoutBasket?.total;
    const refreshedTotal = ((yield select()) as RootState).app.basket.checkoutBasket?.total;
    if (refreshedTotal !== approvedTotal) {
      // The handoff IS saved; only the approved wallet amount is stale. Say that, not
      // "trouble saving your handoff choice".
      sentryLogger.warn('Handoff re-save changed the total of a committed wallet payment', {
        ...logData,
        approvedTotal,
        refreshedTotal,
      });
      yield put(actions.recovery502Triggered('conveyance_total_changed'));
      return 'recovery';
    }
  }
  sentryLogger.info('Handoff mismatch re-saved before submit', logData);
  return 'resaved';
}

/**
 * Trigger Order Submission
 *
 */
export function* orderSubmissionTriggerSaga(
  action: ReturnType<typeof actions.orderSubmissionTrigger>,
) {
  try {
    let state = (yield select()) as RootState;

    // TEAM-14644: if a previous submit on this basket has already failed, re-validate
    // the basket before resubmitting. Without this, a second Place Order press
    // re-sends the identical, unrefreshed payload against server-side state that has
    // already rejected it, so it fails the same way every time. Gated on `submitFailed`
    // so the first attempt is unchanged (the checkout already validates once on mount).
    //
    // ALSO gated on the payment NOT being committed yet, and that gate is the whole
    // safety of this block. There are TWO independent ways an amount gets committed
    // before this saga runs, and both must be excluded.
    //
    // 1. TOAST. `toastPaymentConfirmed()` is dispatched BEFORE
    //    `window.Toast.confirmIntent()` and ORDER_SUBMISSION_TRIGGER is only dispatched
    //    from that call's success callback (toast/submit-button.tsx:110 and :149), so by
    //    the time this saga runs the card is authorized at the pre-validation total and
    //    the intent is immutable. Re-validating calls `basketActions.success(response)`
    //    inside the lock window TEAM-13245 added precisely to stop the total moving
    //    after authorization; the submit would then carry a total the intent does not
    //    match, which chowly-api's strict-equality authorized-vs-total preflight
    //    rejects as Toast 10025 — a failed order plus a lingering pre-auth hold.
    //
    // 2. DIGITAL WALLETS (Apple/Google Pay via Stripe, Square, Olo Pay). These do NOT
    //    set `toastPaymentConfirmed` — that flag is Toast-only. The diner approves a
    //    specific amount in the OS wallet sheet, the hook tokenizes it and dispatches
    //    `orderDigitalWalletTrigger` (stripe/digital-wallet/hooks.ts:100,
    //    square/digitalWallets/hooks.ts:311, olo-pay/digital-wallet/hooks.ts:60), and
    //    `orderSubmissionDigitalWalletTriggerSaga` funnels straight into this saga via
    //    `put(orderSubmissionTrigger(...))` at saga.ts:838. So a wallet retry arrives
    //    here with the amount already committed in the sheet but
    //    `toastPaymentConfirmed` false. Re-validating would move the total and submit an
    //    amount the diner never approved — the same authorize-at-one-amount /
    //    submit-at-another desync as case 1, just via a different processor.
    //    Detected by `card_token` ON a DIGITAL_WALLET `paymentType`, and it takes BOTH.
    //    `paymentType.type === DIGITAL_WALLET` alone is NOT enough: two different
    //    actions produce that type, and only one of them means an amount was approved.
    //      (a) SELECTION, nothing committed — `DIGITAL_WALLET_TOGGLE`
    //          (payment/reducer.ts) writes `{ type: DIGITAL_WALLET,
    //          selectedDigitalWallet }` with NO token. It fires on a radio click AND
    //          automatically on mount for the default wallet
    //          (paymentToggle/index.tsx:68-73), and it fires AGAIN on every submit
    //          failure via the reset effect at paymentToggle/index.tsx:76-84 — so a
    //          diner who picked a saved card and failed a submit arrives at the retry
    //          holding this shape.
    //      (b) COMMITTED — the three wallet hooks build a `PaymentDetails` carrying
    //          `card_token` + `type: DIGITAL_WALLET` only after the OS sheet returns a
    //          token, and `orderSubmissionDigitalWalletTriggerSaga` writes it via
    //          `creditCardSet` before funnelling into this saga.
    //    `card_token` is what separates them, so the gate requires it. Keying on `type`
    //    alone over-matched shape (a) and silently skipped re-validation for exactly
    //    the population this ticket is for (verified: real reducers + the real reset
    //    effect + this saga produced 0 validate calls on the retry).
    //
    // Saved / PCI-proxy credit cards are deliberately NOT excluded: they are charged at
    // submit, not pre-authorized, so re-validating them is both safe and the point.
    //
    // So: re-validation applies HERE only to retries where nothing has been committed yet
    // -- saved card, PCI card, pay-in-store, cash, zero-dollar.
    //
    // TEAM-14644 UAT follow-up: that made this gate unreachable on the Toast path, because
    // the Toast submit button dispatches `toastPaymentConfirmed()` BEFORE handing off to
    // the SDK and ORDER_SUBMISSION_TRIGGER only comes from the SDK's success callback, so
    // the trigger always arrives already authorized. UAT saw the retry resend a
    // byte-identical payload. The Toast path is now re-validated one step EARLIER instead,
    // in the submit button before the intent is authorized (toast/submit-button.tsx,
    // `placeOrder`) -- which is also why it is safe there: the intent is confirmed against
    // the total the validate just wrote, so there is no authorize-at-one-amount /
    // submit-at-another desync to guard against.
    //
    // Still NOT covered anywhere: the three external digital wallets (Stripe / Square /
    // Olo Pay), whose OS sheet commits the amount inside their own hooks before
    // `orderSubmissionDigitalWalletTriggerSaga` funnels in here. Same fix shape applies --
    // validate before the sheet opens -- and it belongs in those hooks, not here.
    //
    // Widening THIS gate to committed payments is still wrong: it needs the desync closed
    // first (see docs/CHECKOUT_PAGE.md, "Failed validates fail CLOSED") and requires
    // reverting BOTH AC5 dispatches below in the same change -- the recovery modal and the
    // unavailable-items orderPending() -- because each re-arms the CTA and both are only
    // safe while nothing committed can reach them.
    const walletAmountCommitted =
      state.app.payment.paymentType?.type === PAYMENT_TYPES.DIGITAL_WALLET &&
      Boolean(state.app.payment.paymentType?.card_token);
    const paymentAlreadyAuthorized =
      Boolean(state.app.payment.toastPaymentConfirmed) || walletAmountCommitted;

    if (state.app.orderStatus.submitFailed && !paymentAlreadyAuthorized) {
      const { basketId, locationId } = getIdsFromState(state);
      if (basketId && locationId) {
        // `settleToPending: false` keeps Place Order disabled through the resubmit
        // (see runBasketValidation) so the retry does not open a double-submit window.
        const validation = yield* runBasketValidation(basketId, locationId, false);
        if (!validation.ok) {
          // Do not submit. What the diner is left looking at depends on the failure
          // class, and only two of the five leave `orderStatus` where this saga put it
          // (TRANSMITTING / ORDER_SUBMISSION_TRIGGERED), which renders the CTA disabled
          // reading "Order updating...." (submitOrder/index.tsx:73 gates on
          // ORDER_PENDING; the copy is submitOrder/pci-proxy/index.tsx:28). Both of
          // those have to be settled here or checkout dead-ends -- AC5 requires the
          // control return to an enabled state.
          //
          //   502 gateway        -> recovery502Triggered('validate'), status ORDER_PENDING
          //   delivery unavail.  -> deliveryUnavailable(), blocking modal, terminal
          //   restaurant unavail.-> restaurantUnavailable(), blocking modal, terminal
          //   pricing unavail.   -> pricingUnavailable(), blocking modal, terminal (TEAM-14493)
          //   basket not found   -> basketNotFound(), inline notice, terminal (TEAM-15690)
          //   invalid email      -> orderStatusMessageErrorSet, status ORDER_PENDING (self-settling)
          //   unavailable items  -> basket state only, orderStatus UNTOUCHED  <- settle
          //   generic error      -> nothing at all                            <- settle
          //
          // The generic class routes to the recovery modal that already exists, whose
          // `validate` copy ("your cart is saved and your card hasn't been charged") is
          // literally accurate here.
          //
          // `validation.surfaced` comes back from the call itself. Do NOT re-derive it
          // from state: `orderStatus.type` and `basket.unavailableBasketItemIds` can
          // both hold values from earlier in the session. Within a live basket nothing
          // clears those ids except the diner removing the items
          // (REMOVE_UNAVAILABLE_BASKET_ITEMS_SUCCESS); DESTROY_BASKET clears them too,
          // but that is not a step a diner takes mid-checkout. So a stale positive would
          // suppress this modal and reproduce the exact strand the branch exists to
          // prevent.
          //
          // READ BEFORE CHANGING THE GATE ABOVE: both branches below set status
          // ORDER_PENDING -- RECOVERY_502_TRIGGERED behind its modal, and orderPending()
          // behind the inline notice (orderStatus/reducer.ts) -- so the CTA becomes
          // pressable again either way. That is safe here ONLY because of the
          // `paymentAlreadyAuthorized` gate -- this branch is unreachable once anything
          // is authorized, so no Toast payment intent exists, so the intent/basket
          // desync that docs/CHECKOUT_PAGE.md warns about cannot occur, and a second
          // press re-enters this same block and re-validates before any submit. Anyone
          // widening the gate to authorized payments MUST reduce both to a bare
          // `return` in the same change, or diners will be able to authorize a stale
          // amount (the TEAM-14043 -> TEAM-14666 regression, ~2x baseline case rate).
          //
          // Both are settled by `settleFailedValidation`: the generic class gets the
          // recovery modal, unavailable-items-only gets orderPending().
          //
          // Unavailable items: the inline notice renders off basket state
          // (items-unavailable-notice.tsx, driven by basket.unavailableBasketItemIds)
          // and never touches `orderStatus`, so without this the diner reads "some
          // items are unavailable", presses Remove Items -- which dispatches only
          // basket actions (basket/saga.ts:434, nothing in that flow re-arms
          // orderStatus) -- and is left on a dead CTA. This mirrors the submit path
          // the retry displaces, which pairs the same notice with orderPending() at
          // saga.ts:1189-1190 (TEAM-12968) for exactly this reason.
          //
          // Deliberately NOT a plain `surfaced` branch: `unavailableItemsOnly` is false
          // when delivery- or restaurant-unavailable also matched (independent `if`s in
          // the same catch, so one error can carry both), because settling to
          // ORDER_PENDING there would dismiss a blocking modal that is terminal by
          // design.
          //
          // Safe for the same reason the recovery modal is: the
          // `paymentAlreadyAuthorized` gate makes this path unreachable once anything
          // is authorized, so there is no payment intent to desync (see the gate
          // comment, and docs/CHECKOUT_PAGE.md "Failed validates fail CLOSED"). Unlike
          // the TEAM-14043 -> TEAM-14666 regression this is not a silent re-arm either:
          // the notice stays on screen and names what the diner has to do.
          yield* settleFailedValidation(validation);
          return;
        }
        // Re-read the refreshed basket so the submit carries the new totals rather than
        // the stale ones that were just rejected. Safe here only because nothing has
        // been authorized against the old total on this path.
        state = (yield select()) as RootState;
      } else {
        // A failed submit implies a basket existed, so missing ids means the basket
        // state is malformed. Log it and fall through to the normal submit rather than
        // stranding the diner — but this is the one path that can still resubmit
        // without re-validating.
        sentryLogger.warn('Skipped pre-resubmit validation: basket ids unavailable', {
          ...getBasketLogData(state.app.basket.checkoutBasket),
        });
      }
    }

    // TEAM-15341 (AC3/AC4): re-save a real handoff mismatch once before giving up on it. Only
    // with a basket, never after a Toast authorization, and for a committed wallet amount only
    // if the total does not move — see the helper.
    if (state.app.payment.toastPaymentConfirmed && getConveyanceCheck(state) === 'mismatch') {
      // Belt and braces: the Toast submit button re-saves a mismatch before it authorizes
      // (`preAuthValidateSaga`), so an authorized Toast payment should never get here
      // mismatched. If one does, the intent is fixed at the authorized total and must not be
      // re-saved against; show the cart-preserving recovery modal (carrying the hold-release
      // flag) instead of the refresh-only throw in `assembleOrderDataFromState` (AC4).
      sentryLogger.warn('Handoff mismatch reached submit after Toast authorization', {
        ...getBasketLogData(state.app.basket.checkoutBasket),
        conveyanceModeType: state.app.conveyanceMode?.type,
        basketConveyanceType: state.app.basket.checkoutBasket?.conveyance_type?.type,
      });
      yield put(actions.recovery502Triggered('conveyance', /* toastPaymentWasAuthorized */ true));
      return;
    }
    if (!state.app.payment.toastPaymentConfirmed) {
      const reconciled = yield* reconcileConveyanceBeforeSubmit(
        state,
        walletAmountCommitted,
        /* repriceAfterResave */ true,
      );
      if (reconciled === 'recovery') return;
      if (reconciled === 'resaved') state = (yield select()) as RootState;
    }

    const orderData = assembleOrderDataFromState(state);

    const filteredOrderData = orderData.payment.filter(
      (payment: PaymentDetails) => payment.type !== PAYMENT_TYPES.STORED_VALUE,
    );

    const storedValue = orderData.payment?.find(
      (payment: PaymentDetails) => payment.type === PAYMENT_TYPES.STORED_VALUE,
    );

    orderData.payment = filteredOrderData;

    if (storedValue && typeof storedValue.amount === 'number') {
      orderData.gift_card = {
        amount_authorized: storedValue.amount,
      };
    }

    yield put(actions.orderSubmissionStart(orderData, action.captcha, action.queryClient));
  } catch (err) {
    const error = err as Error;
    // TEAM-14291: on the Toast path, the card is already authorized when this saga runs
    // (see comment at line 1312). Carry the flag so the diner sees hold-release messaging.
    const state = (yield select()) as RootState;
    const toastPaymentWasAuthorized = Boolean(state.app.payment.toastPaymentConfirmed);
    yield put(
      actions.orderSubmissionError(error.message, undefined, undefined, toastPaymentWasAuthorized),
    );

    // Fire Error Event
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.TRIGGER_SUBMIT_CHECKOUT_ERROR,
      details: error.message,
    });
  }
}

/**
 * Fire post-submit analytics in a detached saga (via `spawn`).
 * Errors here never propagate to the parent — the order is already
 * confirmed and the diner is navigating to the confirmation page.
 * @see TEAM-12605
 */
function* postSubmitAnalyticsSaga(
  orderDetails: OrderDetails,
  basketId: string,
  locationId: number,
) {
  try {
    const orderId = orderDetails.order_id;

    sentryLogger.info('Order Submitted Successfully', {
      basketId,
      ...getCompletedOrderLogData(orderDetails),
    });

    const payment = orderDetails?.order_data.payment[0];
    safeHeapTrack('Order Submitted Successfully', {
      orderId,
      basketId,
      locationId,
      paymentType: payment?.type || 'none',
      selectedDigitalWallet: payment?.selectedDigitalWallet,
      digitalWallet: payment?.digital_wallet_id
        ? paymentReducerDigitalWalletMap[payment.digital_wallet_id]
        : 'none',
    });

    yield put(commerceActions.commerceCheckoutPurchase(orderDetails));
  } catch (error) {
    ErrorReporter.captureException(error, 'warning', {
      eventName: 'POST_SUBMIT_ANALYTICS_FAILURE',
      basketId,
      orderId: orderDetails.order_id,
    });
  }
}

/**
 * Start Order Submission
 *
 */
export function* orderSubmissionStartSaga(action: ReturnType<typeof actions.orderSubmissionStart>) {
  const state = (yield select()) as RootState;
  const queryClient = action.queryClient;

  const orderStatus = state.app.orderStatus;
  const checkoutBasket = state.app.basket.checkoutBasket;
  const payment = state.app.payment;
  const { toast } = state.app.payment;
  if (orderStatus.utm_source) {
    action.submitBasketData.utm_source = orderStatus.utm_source;
  }

  const client = createHttpClient({ origin: getOrigin(window.location.host) });
  const { basketId, locationId } = getIdsFromState(state);

  sentryLogger.info('Start Order Submission', {
    ...getBasketLogData(checkoutBasket),
    ...getPaymentLogData(payment),
  });

  try {
    const orderDetails = (yield call(
      submitOrder,
      {
        basketId,
        locationId,
        orderData: action.submitBasketData,
        captcha: action.captcha,
      },
      { client },
    )) as OrderDetails;

    // Check order status
    if (orderDetails.status_id === OrderStatus.IN_PROGRESS) {
      // Order is in progress, do nothing
      return;
    }

    // Write submitted basket to redux
    yield put(basketActions.confirmation());

    // Write order details to redux for further use in order-confirmation page
    yield put(globalActions.clearFeatureBag());

    const orderId = orderDetails?.order_id;
    if (queryClient) {
      queryClient.setQueryData([queryKeys.orderConfirmation, orderId], orderDetails);
    }

    // Fire analytics in a detached saga (TEAM-12605). Using `spawn` ensures
    // errors can never propagate back to this saga or trigger the outer catch.
    yield spawn(postSubmitAnalyticsSaga, orderDetails, basketId, locationId);

    // Reset order status
    yield put(actions.destroyOrder());

    // TEAM-15276: a cart saved for later is the one just ordered (or an older one) — it must not
    // come back in the next tab.
    clearSavedCart();

    // Go to confirmation page
    const urlObj: UrlObject = {
      pathname: ROUTES.ORDER_CONFIRMATION,
      query: { id: orderDetails.order_id },
    };

    void Router.push(urlObj);
  } catch (error) {
    // TEAM-14291: whether the diner's card was authorized before this failure. Used by
    // every error surface below so the diner can be told the hold releases automatically.
    const toastPaymentWasAuthorized = Boolean(payment.toastPaymentConfirmed);

    // TEAM-14331: the diner's bank wants the card verified (3DS/SCA). coo-api answers 402 with the
    // challenge; run it and resubmit rather than failing. Stripe card payments only — the challenge
    // is a Stripe intent and the resume can only carry it on a tokenized card, never a wallet — and
    // never on the resubmit itself, which cannot be challenged twice (chowly-api turns an unverified
    // resume into a terminal code 11). Anything else, including a 402 whose challenge is unusable,
    // falls through to the paths below unchanged.
    const isResume = isVerificationResume(action.submitBasketData);
    const verificationChallenge = getPaymentVerificationChallenge(error);
    if (
      verificationChallenge &&
      state.app.paymentTypes?.credit_card_token?.provider === PAYMENT_PROVIDER.STRIPE &&
      hasCardTokenPayment(action.submitBasketData) &&
      !isResume
    ) {
      yield call(paymentVerificationSaga, verificationChallenge, action);
      return;
    }

    // TEAM-14331: a failed resume leaves leg 1's spent single-use token in redux, and only code 11's
    // text trips the decline reset below — code 9 (chowly-api's 30-minute sweep), 12 (a concurrent
    // resume), a 400 bad_request or a transport failure would re-enable Place Order on a token that
    // can only fail again. Reset it on every failed resume so the card form comes back.
    if (isResume) {
      yield put(paymentActions.paymentTypeReset());
    }

    // TEAM-15690: coo-api has lost the basket, so neither a resubmit nor the generic copy below
    // ("Please try again") can succeed. End on the "Start a new cart" notice; it is reported there,
    // so SUBMIT_CHECKOUT_ERROR is not captured for it. `toastPaymentWasAuthorized` rides along so the
    // notice keeps the card-hold note this path's generic copy appended (TEAM-14291).
    if (
      yield* surfaceBasketNotFound(error, {
        basketId,
        locationId,
        source: 'submit',
        toastPaymentWasAuthorized,
      })
    ) {
      return;
    }

    // Any transient "couldn't reach the ordering service" failure — a retryable
    // status (RETRYABLE_STATUSES in utils/networkErrors), a browser network drop
    // ("Load failed" / "Failed to fetch"), or a non-JSON gateway/CDN page — routes
    // to the service-unreachable recovery modal instead of the bare submission
    // error. This widens TEAM-12817's 502-only gate so a 504 or network abort no
    // longer collapses to OrderStatusMessages.ORDER_SUBMISSION_ERROR (and,
    // for network errors, no longer concatenates the raw exception text into
    // diner-facing copy via prepareErrorMessage). The conveyance catches above stay on
    // GATEWAY_STATUSES by design (TEAM-14612); validate was widened under TEAM-14320 to
    // network errors and non-JSON pages only — deliberately not the full retryable set.
    if (isRetryableError(error)) {
      yield put(actions.recovery502Triggered('submit', toastPaymentWasAuthorized));
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.SUBMIT_CHECKOUT_ERROR,
      });
      sentryLogger.error('Order submission failed with a retryable/network error', {
        ...getBasketLogData(checkoutBasket),
        status: (error as { status?: number })?.status,
        errorName: error instanceof Error ? error.name : undefined,
        paymentType: payment.paymentType?.type,
        digitalWallet: payment.selectedDigitalWallet,
      });
      return;
    }

    // Error Notification
    const errorResponse = (yield call(
      prepareErrorMessage,
      OrderStatusMessages.ORDER_SUBMISSION_ERROR,
      error,
    )) as SagaReturnType<typeof prepareErrorMessage>;
    const errorType = errorResponse?.error?.error;

    // ASAP passive menu rollover: the active menu can change while the diner is
    // in checkout, so the unavailable-items error can surface at submit (not just
    // init/validate). Surface the inline State 1 / State 2 notice and return to
    // the checkout view instead of the generic submission error (TEAM-12968).
    const unavailableBasketItemIds = getUnavailableBasketItemIds(errorResponse);
    if (unavailableBasketItemIds.length) {
      yield put(basketActions.setUnavailableBasketItems(unavailableBasketItemIds, errorType));
      yield put(actions.orderPending());
      // Expected, handled, recoverable flow — log at warn to avoid alert noise.
      sentryLogger.warn('Order submission blocked by unavailable items', {
        eventName: ERROR_MESSAGES.BASKET_NOT_VALID,
        ...getBasketLogData(checkoutBasket),
        unavailableBasketItemIds,
      });
      return;
    }

    /**
     * TEAM-13882: every classification below reads `errorResponse.detail` — the raw upstream text —
     * not `errorResponse.message`, which is now the resolved diner copy. The substrings these
     * predicates look for (`isPaymentError`'s seven, "toast payment intent error", the
     * location-not-available wording) only ever appear in the backend's `error_description`.
     *
     * Getting this wrong is not cosmetic: if `isPaymentError` silently returns false, a card failure
     * routes to the status bar instead of the full page AND `paymentTypeReset()` below never fires,
     * so a stale single-use Stripe/Square token stays in redux and the diner's retry hard-fails with
     * "You cannot use a Stripe token more than once".
     */
    const errorDetail = errorResponse.detail;

    // TEAM-14645: classify the parsed cause once, then key the funds-total copy remap, the
    // page-error routing, and the Place Order gate off it. classifySubmitFailure reads the same raw
    // `detail` (per TEAM-13882), so the cause stays consistent with the checks below.
    const cause = classifySubmitFailure(errorResponse, {
      toastInitialized: Boolean(toast?.initialized),
    });

    /**
     * TEAM-12668: resolved once, because the copy the diner reads and the decision to reset the
     * single-use payment token must come from the same verdict. `resolveDeclineCopy` is pure, so
     * hoisting it changes nothing except that the two uses below cannot disagree.
     *
     * Deliberately NOT folded into `cause`. `classifySubmitFailure` derives PAYMENT_DECLINED from
     * bare `isPaymentError`, which misses most real declines (see the token-reset gate below).
     * Widening `cause` to this match would also move those declines onto the page-error surface and
     * make them terminal for `shouldBlockPlaceOrder` — TEAM-12668 AC7 requires each error to reach
     * the surface it reaches today, so this ticket changes the copy and the token reset, never the
     * routing.
     */
    const declineCopy = resolveDeclineCopy(errorDetail);

    // TEAM-14292: at submit time the email check takes priority over restaurant-unavailable
    // because the email is the actionable problem. Uses orderSubmissionError (not
    // orderStatusMessageErrorSet) because the submission has already been attempted.
    // Different copy from validate: at this point the card may already be authorized, so the
    // diner needs to know about the potential temporary hold (TEAM-14292 run 3).
    //
    // TEAM-14292 run 4: `user_message` from the already-parsed envelope if present, otherwise
    // the submit-specific fallback (not a second `prepareErrorMessage` call — the Response
    // body is already consumed).
    //
    // Ordered ahead of the TEAM-12668 decline copy below for the same reason: an unusable email
    // is the actionable problem, and a decline that also carries one would otherwise be told to
    // fix its card when the email is what the submission actually rejected.
    if (isInvalidEmailError(error, errorResponse)) {
      yield put(
        actions.orderSubmissionError(
          errorResponse.error?.user_message || INVALID_EMAIL_SUBMIT_MESSAGE,
          cause,
          {
            href: ROUTES.ACCOUNT,
            label: 'Go to Account Settings',
          },
        ),
      );
    } else if (isRestaurantUnavailableError(errorResponse)) {
      yield put(
        actions.restaurantUnavailable(
          getRestaurantUnavailableReason(errorResponse),
          toastPaymentWasAuthorized,
        ),
      );
    } else {
      /**
       * TEAM-12668: a card decline names a reason the diner can act on — a wrong billing ZIP, a
       * mistyped CVV, an expired card — so it gets that reason's copy rather than the one generic
       * submission sentence. `resolveDeclineCopy` returns `null` for anything it does not recognise
       * as a decline, which falls through to `errorResponse.message`, the TEAM-13882 resolved copy.
       * Both operands are authored strings, so no provider text and no `x-NNNN` card reference can
       * reach the diner either way.
       *
       * The funds-total remap is keyed on `cause` (TEAM-14645) rather than its own substring test,
       * and stays first: FUNDS_TOTAL is a total-changed failure, not a decline, and must keep its
       * own retryable copy even when the provider string also looks like one.
       */
      // TEAM-14291: when a Toast authorization was placed before this failure, append the
      // hold-release note to the resolved diner copy so actionable reasons (location
      // unavailable, basket invalid, etc.) are preserved — not replaced.
      const baseCopy =
        cause === SubmitFailureCause.FUNDS_TOTAL
          ? 'There was an issue applying your payment total. Please reset and try again.'
          : declineCopy ?? errorResponse.message;

      const userFacingErrorMessage = toastPaymentWasAuthorized
        ? `${baseCopy}${TOAST_HOLD_RELEASE_NOTE}`
        : baseCopy;

      // Toast payment-intent errors and card declines keep routing to the page-error
      // (PlaceOrderError) surface; every other cause stays on the inline status bar. Keyed on the
      // parsed cause (TEAM-14645), which reads the same raw `detail` the old substring checks did.
      if (
        cause === SubmitFailureCause.TOAST_PAYMENT_INTENT ||
        cause === SubmitFailureCause.PAYMENT_DECLINED
      ) {
        yield put(
          actions.orderSubmissionPageError(
            userFacingErrorMessage,
            cause,
            toastPaymentWasAuthorized,
          ),
        );
      } else {
        yield put(
          actions.orderSubmissionError(
            userFacingErrorMessage,
            cause,
            undefined,
            toastPaymentWasAuthorized,
          ),
        );
      }
    }

    // Derive event
    let gaAction = gaActions.checkoutError;
    let kaEventName = ERROR_MESSAGES.SUBMIT_CHECKOUT_ERROR;

    /**
     * Credit card-specific errors — reset single-use token on payment failure. Reads the raw
     * `errorDetail` (TEAM-13882); kept a separate check from the cause routing above so the reset +
     * GA still fire even when the cause routed elsewhere (TEAM-14645).
     *
     * TEAM-12668 widened this gate, and **only** this one. `isPaymentError`'s seven substrings miss
     * most real declines: "GENERIC_DECLINE" lowercases to "generic_decline", which does not contain
     * "declined", so six of Square's seven reason codes fall through, as do the POS strings "Credit
     * card was not approved" and "Invalid CVV Code". Those declines were leaving the single-use
     * Stripe/Square token in redux, so the diner's retry hard-failed with "You cannot use a Stripe
     * token more than once" — the failure docs/DINER_ERROR_MESSAGES.md names as the severe one.
     *
     * The second operand is derived from the decline mapping rather than a second hand-maintained
     * substring list, so the two cannot drift apart.
     *
     * The routing gate above is keyed on `cause`, whose PAYMENT_DECLINED arm is bare `isPaymentError`
     * — so the surface each error reaches is unchanged by this widening, as TEAM-12668's AC7
     * requires. A decline matched only by `resolveDeclineCopy` gets the specific copy and the token
     * reset, and still lands on the inline status bar exactly as it does today.
     *
     * Note this block also sets `gaAction` and `kaEventName`, so the newly-matched declines move
     * from `checkoutError`/`SUBMIT_CHECKOUT_ERROR` to `paymentDenied`/`PAYMENT_DENIED_ERROR`. That
     * is the correct classification — they are payment denials — but it shifts the counts on any
     * dashboard built from the old split, so it is a deliberate consequence rather than a side
     * effect nobody noticed.
     */
    if (isPaymentError(errorDetail) || declineCopy !== null) {
      if (state.app.paymentTypes?.credit_card_token.supported) {
        yield put(paymentActions.paymentTypeReset());
      }

      gaAction = gaActions.paymentDenied;
      kaEventName = ERROR_MESSAGES.PAYMENT_DENIED_ERROR;
    }

    // Ordering not available at location
    // e.g. location_not_available: Online ordering is unavailable at this time. Please use the location finder to try another location nearby.
    // e.g. Your order could not be placed. Please visit this location directly to place your order.
    if (
      errorType === 'location_not_available' ||
      errorDetail.includes(
        'Your order could not be placed. Please visit this location directly to place your order.',
      ) ||
      errorDetail.includes('We are currently unable to take your order.')
    ) {
      gaAction = gaActions.locationNotAvailable;
      kaEventName = ERROR_MESSAGES.LOCATION_NOT_AVAILABLE;
    }

    // Basket is invalid
    // e.g. Your order cannot be ready by 8:30pm due to the time it will take to prepare your order. Please choose another available time.
    // e.g. Apologies, there is a problem on this order. Please update your order to remove any unavailable items to try this order again. "Vanilla" is not currently available on "Shakes".
    if (errorType === 'basket_not_valid') {
      gaAction = gaActions.basketNotValid;
      kaEventName = ERROR_MESSAGES.BASKET_NOT_VALID;
    }

    /**
     * GA Event. The label is built from `detail`, not the resolved copy: GA labels are not
     * diner-facing, and the resolved copy collapses every failure onto the same sentence, which
     * would destroy this dimension. Capped so a long upstream body can't blow the label limit.
     */
    fireGaEvent(gaCats.order, gaAction, {
      label: `${errorType ?? 'error'}: ${errorDetail.slice(0, GA_LABEL_DETAIL_MAX_LENGTH)}`,
    });

    sentryLogger.error('Order submission failed', {
      eventName: kaEventName,
      ...getBasketLogData(checkoutBasket),
      paymentType: payment.paymentType?.type,
      digitalWallet: payment.selectedDigitalWallet,
      // TEAM-14331: a 402 that fell through carries the Stripe `client_secret` in its body.
      error: redactVerificationSecret(errorResponse),
      details: redactVerificationSecret(typeof error === 'string' ? error : JSON.stringify(error)),
    });
  }
}

export default function* rootSaga() {
  yield takeLatest(actions.INITIALIZE_ORDER, initializeOrderSaga);
  yield takeLatest(actions.POST_INITIALIZE_ORDER, postInitializeOrderSaga);
  // takeLeading (not takeLatest): validateBasket is a POST with server-side effects and no abort
  // signal, so cancelling the saga would NOT stop an in-flight request — takeLatest would let a
  // burst issue concurrent mutating /validate calls that can land out of order (the TEAM-13245/10025
  // race class). takeLeading keeps a burst to a single in-flight validate. Coalescing rapid tip
  // re-validates so the newest tip wins without firing concurrent POSTs is tracked as a follow-up
  // (see the TEAM-14818 review thread).
  yield takeLeading(actions.VALIDATE_ORDER, validateOrderSaga);
  // takeLeading for the same reason as VALIDATE_ORDER above: validateBasket is a POST with
  // server-side effects and no abort signal.
  yield takeLeading(actions.PRE_AUTH_VALIDATE, preAuthValidateSaga);
  yield takeLatest(actions.TIP_ADD, tipAddSaga);
  yield takeLatest(actions.UTENSILS_SET, utensilsSetSaga);
  yield takeLatest(actions.BASKET_INSTRUCTIONS_SET, basketInstructionsSetSaga);
  yield takeLatest(actions.ORDER_SUBMISSION_START, orderSubmissionStartSaga);
  yield takeLatest(
    actions.ORDER_SUBMISSION_DIGITAL_WALLET_TRIGGER,
    orderSubmissionDigitalWalletTriggerSaga,
  );
  yield takeLatest(actions.ORDER_SUBMISSION_TRIGGER, orderSubmissionTriggerSaga);
}
